import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import UserLogin from './components/UserLogin'
import Disclaimer from './components/Disclaimer'
import DataTable from './components/previsional/DataTable'
import { computeActaHash } from './utils/actaHash'
import { useCoins } from './hooks/useCoins'
import { useAuth } from './hooks/useAuth'
import { useTickets } from './hooks/useTickets'
import { useNormative } from './hooks/useNormative'
import { useCaseData, normalizePeriodoKey, parseMontoValue, toYmNum, ymNumToKey, toPeriodoKeyFromAny, toActividadKeySet, getActividadYm, extractPdfHeaderCese, toFechaInputFromPeriodoKey, labelActividadMes, normalizeReviewRows, buildMissingRows } from './hooks/useCaseData'
import { useIndices } from './hooks/useIndices'
import useNormativeConfig from './hooks/useNormativeConfig'
import { seedNormativeConfig } from './scripts/seedNormativeConfig'
import { useProcessingPayload } from './hooks/useProcessingPayload'
import AnimatedBackground from './components/layout/AnimatedBackground'
import { motion } from 'framer-motion'
import Landing from './components/Landing'
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore'
import { db } from './firebase-config'
import CoinMarketplace from './components/CoinMarketplace'
import ContactForm from './components/ContactForm'
import UserTicketsModal from './components/UserTicketsModal'
import aplicarNormativaAnses from './core/aplicarNormativaAnses'
import actualizarPorIndice from './core/actualizarRemuneraciones'

import composeDictamenContext from './core/dictamenComposer'
import { fetchDictamen } from './services/geminiService'
import DictamenPreview from './components/previsional/DictamenPreview'
import DashboardResultados from './components/DashboardResultados'
import { getSerieStatus, JURISDICCIONES_DISPONIBLES, TASAS_DISPONIBLES } from './services/tasasInteresService'
import { sanitizarRemuneraciones } from './core/engine/procesarAnses'

import strategicEvaluator from './core/engine/strategicEvaluator'
import { computeShadowBilling, extractResumenTotalesPorCuit } from './core/engine/shadowBilling'
import { evaluateLaborIntegrity } from './core/engine/laborIntegrity'
import { buildPreEmissionAudit } from './core/engine/preEmissionAudit'
import { evaluateLegalConsistency } from './core/engine/legalConsistency'
import CORE_RULES_CONFIG from './core/rules/config'
import StrategicAlerts from './components/StrategicAlerts'
import ConfirmModal from './components/ConfirmModal'
import AnalisisDeEstrategia from './components/AnalisisDeEstrategia'
import ResultHero from './components/ResultHero'
import useSimulationMode from './hooks/useSimulationMode'

let getIndices, ensureIndicesForRange, subscribeIndicesUpdated,
    evaluateHaberMaximoConfiscatoriedad, applySimulationPayload, parseMonedaLocale
import { useCaseValidation } from './hooks/useCaseValidation'
import useMutationAudit from './hooks/useMutationAudit'
import useWizard from './hooks/useWizard'
import useStep4 from './hooks/useStep4'
import useResultsView from './hooks/useResultsView'
import ResultsAlerts from './components/ResultsAlerts'
import ResultadosTabBasica from './components/ResultadosTabBasica'
import ResultadosTabCompleta from './components/ResultadosTabCompleta'
import useCitizenData from './hooks/useCitizenData'
import useLawyerData from './hooks/useLawyerData'
import ResultsShell from './components/ResultsShell'
import GuideSidebar from './components/GuideSidebar'
import MutationAuditAccordion from './components/MutationAuditAccordion'
import MutationReasonModal from './components/MutationReasonModal'
import HistorialModeSelector from './components/HistorialModeSelector'
import HistorialReviewShell from './components/HistorialReviewShell'
import ProcesamientoInfoPanel from './components/ProcesamientoInfoPanel'
import EdicionToolbar from './components/EdicionToolbar'
import YearlyLaborTable from './components/YearlyLaborTable'
import ProcesamientoClinicalPanels from './components/ProcesamientoClinicalPanels'
import Step4Footer from './components/Step4Footer'
import Step4Panel from './components/Step4Panel'
import EstrategiaJudicialSection from './components/EstrategiaJudicialSection'
import AppModals from './components/AppModals'

const COINS_POR_CALCULO = 2
const COINS_POR_DICTAMEN = 1

export default function App() {
  const EXPEDIENTE_STORAGE_KEY = 'motor_previsional_expediente'
  const CASE_FINGERPRINT_KEY = 'motor_previsional_case_fingerprint'
  const INDICES_CACHE_PREFIX = 'indices_previsionales_cache_v2'
  const COLOR_PROFILES = {
    'profesional-claro': {
      label: 'Profesional Claro',
      isDark: false,
      vars: {
        '--bg-page': '#F8FAFC', '--bg-page-accent': '#E2E8F0', '--bg-surface': 'rgba(255,255,255,0.9)', '--bg-surface-strong': '#FFFFFF', '--bg-panel': 'rgba(241,245,249,0.94)',
        '--text-primary': '#0F172A', '--text-secondary': '#334155', '--text-muted': '#64748B', '--text-inverse': '#F8FAFC',
        '--border-soft': 'rgba(15,23,42,0.18)', '--border-strong': 'rgba(15,23,42,0.32)', '--focus-ring': '#0284C7',
        '--accent': '#0284C7', '--accent-2': '#0284C7', '--ok': '#16A34A', '--warn': '#B45309', '--danger': '#B91C1C',
        '--accent-soft-bg': 'rgba(2,132,199,0.14)', '--ok-soft-bg': 'rgba(22,163,74,0.14)', '--warn-soft-bg': 'rgba(180,83,9,0.16)', '--contrast-on-accent': '#F8FAFC', '--contrast-on-accent-2': '#0F172A',
      },
    },
    impresionante: {
      label: 'Impresionante',
      isDark: false,
      vars: {
        '--bg-page': '#F8F9FA', '--bg-page-accent': '#E2E8F0', '--bg-surface': 'rgba(255,255,255,0.92)', '--bg-surface-strong': '#FFFFFF', '--bg-panel': 'rgba(248,250,252,0.94)',
        '--text-primary': '#0A192F', '--text-secondary': '#475569', '--text-muted': '#64748B', '--text-inverse': '#F8FAFC',
        '--border-soft': 'rgba(10,25,47,0.16)', '--border-strong': 'rgba(10,25,47,0.28)', '--focus-ring': '#D97706',
        '--accent': '#D97706', '--accent-2': '#D97706', '--ok': '#16A34A', '--warn': '#B45309', '--danger': '#B91C1C',
        '--accent-soft-bg': 'rgba(217,119,6,0.16)', '--ok-soft-bg': 'rgba(22,163,74,0.14)', '--warn-soft-bg': 'rgba(180,83,9,0.18)', '--contrast-on-accent': '#F8FAFC', '--contrast-on-accent-2': '#0A192F',
      },
    },
    moderno: {
      label: 'Moderno',
      isDark: false,
      vars: {
        '--bg-page': '#FAFAFA', '--bg-page-accent': '#E4E4E7', '--bg-surface': 'rgba(255,255,255,0.92)', '--bg-surface-strong': '#FFFFFF', '--bg-panel': 'rgba(250,250,250,0.96)',
        '--text-primary': '#18181B', '--text-secondary': '#3F3F46', '--text-muted': '#71717A', '--text-inverse': '#F8FAFC',
        '--border-soft': 'rgba(24,24,27,0.14)', '--border-strong': 'rgba(24,24,27,0.24)', '--focus-ring': '#0D9488',
        '--accent': '#0D9488', '--accent-2': '#7C3AED', '--ok': '#16A34A', '--warn': '#B45309', '--danger': '#B91C1C',
        '--accent-soft-bg': 'rgba(13,148,136,0.16)', '--ok-soft-bg': 'rgba(22,163,74,0.14)', '--warn-soft-bg': 'rgba(124,58,237,0.16)', '--contrast-on-accent': '#F8FAFC', '--contrast-on-accent-2': '#18181B',
      },
    },
    tecnico: {
      label: 'Tecnico',
      isDark: false,
      vars: {
        '--bg-page': '#F1F5F9', '--bg-page-accent': '#E2E8F0', '--bg-surface': 'rgba(255,255,255,0.92)', '--bg-surface-strong': '#FFFFFF', '--bg-panel': 'rgba(241,245,249,0.95)',
        '--text-primary': '#334155', '--text-secondary': '#475569', '--text-muted': '#64748B', '--text-inverse': '#F8FAFC',
        '--border-soft': 'rgba(51,65,85,0.2)', '--border-strong': 'rgba(51,65,85,0.32)', '--focus-ring': '#4F46E5',
        '--accent': '#4F46E5', '--accent-2': '#64748B', '--ok': '#16A34A', '--warn': '#B45309', '--danger': '#B91C1C',
        '--accent-soft-bg': 'rgba(79,70,229,0.16)', '--ok-soft-bg': 'rgba(22,163,74,0.14)', '--warn-soft-bg': 'rgba(100,116,139,0.2)', '--contrast-on-accent': '#F8FAFC', '--contrast-on-accent-2': '#334155',
      },
    },
    'abogado-previsional-moderno': {
      label: 'Abogado Previsional Moderno',
      isDark: false,
      vars: {
        '--bg-page': '#F8FAFC', '--bg-page-accent': '#EEF2F7', '--bg-surface': 'rgba(255,255,255,0.96)', '--bg-surface-strong': '#FFFFFF', '--bg-panel': 'rgba(246,248,252,0.98)',
        '--text-primary': '#0F172A', '--text-secondary': '#334155', '--text-muted': '#64748B', '--text-inverse': '#F8FAFC',
        '--border-soft': 'rgba(15,23,42,0.16)', '--border-strong': 'rgba(15,23,42,0.28)', '--focus-ring': '#8C6A2D',
        '--accent': '#0B1F3A', '--accent-2': '#8C6A2D', '--ok': '#166534', '--warn': '#92400E', '--danger': '#B91C1C',
        '--accent-soft-bg': 'rgba(11,31,58,0.12)', '--ok-soft-bg': 'rgba(22,101,52,0.14)', '--warn-soft-bg': 'rgba(146,64,14,0.14)', '--contrast-on-accent': '#F8FAFC', '--contrast-on-accent-2': '#0F172A',
      },
    },
    'profesional-oscuro': {
      label: 'Profesional Oscuro',
      isDark: true,
      vars: {
        '--bg-page': '#0B0F19', '--bg-page-accent': '#111827', '--bg-surface': 'rgba(30,41,59,0.78)', '--bg-surface-strong': 'rgba(30,41,59,0.9)', '--bg-panel': 'rgba(15,23,42,0.78)',
        '--text-primary': '#F1F5F9', '--text-secondary': '#CBD5E1', '--text-muted': '#94A3B8', '--text-inverse': '#0B0F19',
        '--border-soft': 'rgba(241,245,249,0.2)', '--border-strong': 'rgba(241,245,249,0.35)', '--focus-ring': '#38BDF8',
        '--accent': '#38BDF8', '--accent-2': '#38BDF8', '--ok': '#4ADE80', '--warn': '#FBBF24', '--danger': '#F87171',
        '--accent-soft-bg': 'rgba(56,189,248,0.18)', '--ok-soft-bg': 'rgba(74,222,128,0.2)', '--warn-soft-bg': 'rgba(251,191,36,0.2)', '--contrast-on-accent': '#0B0F19', '--contrast-on-accent-2': '#0B0F19',
      },
    },
  }
  const DEFAULT_PROFILE = 'profesional-claro'
  const auth = useAuth()
  const [openTickets, setOpenTickets] = useState(0)
  const [engineReady, setEngineReady] = useState(false)

  useEffect(() => {
    Promise.all([
      import('./core/aplicarNormativaAnses'),
      import('./core/updates/actualizarRemuneraciones'),
      import('./services/indicesService'),
      import('./core/dictamenComposer'),
      import('./services/geminiService'),
      import('./services/tasasInteresService'),
      import('./core/engine/procesarAnses'),
      import('./services/indexUpdater'),
      import('./core/engine/strategicEvaluator'),
      import('./core/engine/shadowBilling'),
      import('./core/engine/laborIntegrity'),
      import('./core/engine/preEmissionAudit'),
      import('./core/engine/legalConsistency'),
      import('./core/rules/config'),
      import('./core/engine/haberMaximoLegal'),
      import('./simulation/adapter'),
      import('./utils/sanitizacion'),
    ]).then(([
      m1, m2, m3, m4, m5, m6, m7, m8, m9, m10, m11, m12, m13, m14, m15, m16, m17
    ]) => {
      getIndices = m3.getIndices
      ensureIndicesForRange = m8.ensureIndicesForRange; subscribeIndicesUpdated = m8.subscribeIndicesUpdated
      evaluateHaberMaximoConfiscatoriedad = m15.evaluateHaberMaximoConfiscatoriedad
      applySimulationPayload = m16.applySimulationPayload
      parseMonedaLocale = m17.parseMonedaLocale
      setEngineReady(true)
    })
  }, [])
  const [progressLabel, setProgressLabel] = useState('')
  const createBaseExpediente = () => ({
      nombre: '',
      apellido: '',
      cuilCuit: '',
      fechaNacimiento: '',
      sexo: 'F',
      cantidadHijos: '',
      hijosAdoptivos: '',
      hijosDiscapacidad: '',
      cobraAUH: 'no',
      fechaCese: '',
      fechaOtorgamientoBeneficio: '',
      fechaReclamoAdministrativo: '',
      fechaDenegatoriaODemanda: '',
      tieneBeneficioPrevio: false,
      esDocente: false,
      datosDocentes: {
        cargoCese: '',
        sueldoBrutoCargoCese: '',
        mesesEnCargoContinuos: 0,
        mesesEnCargoAlternados: 0,
      },
      datosPension: {
        tipoPension: '',
        fechaFallecimiento: '',
        haberJubiladoCausante: '',
        concurreConyugeConviviente: true,
        concurrentes: { conyuge: true, hijosCount: 0 },
      },
      fallo: 'ISBIC',
      haberActual: '',
    })
  const [expediente, setExpediente] = useState(() => {
    const base = createBaseExpediente()
    try {
      const saved = localStorage.getItem(EXPEDIENTE_STORAGE_KEY)
      if (!saved) return base
      const parsed = JSON.parse(saved)
      return {
        ...base,
        ...(parsed || {}),
        datosDocentes: {
          ...base.datosDocentes,
          ...((parsed && parsed.datosDocentes) || {}),
        },
        datosPension: {
          ...base.datosPension,
          ...((parsed && parsed.datosPension) || {}),
          concurrentes: {
            ...base.datosPension.concurrentes,
            ...((parsed && parsed.datosPension && parsed.datosPension.concurrentes) || {}),
          },
        },
      }
    } catch {
      return base
    }
  })
  const [rows, setRows] = useState([])
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [showTicketsModal, setShowTicketsModal] = useState(false)
  const [hasAdminUpdates, setHasAdminUpdates] = useState(false)
  const [ticketsSeenAt, setTicketsSeenAt] = useState(() => Number(localStorage.getItem('ticketsSeenAt') || 0))
  const [indicesUnavailable, setIndicesUnavailable] = useState(false)
  const [showDictamen, setShowDictamen] = useState(false)
  const [dictamenLoading, setDictamenLoading] = useState(false)
  const [dictamenText, setDictamenText] = useState('')
  const [dictamenVerificationCode, setDictamenVerificationCode] = useState('')
  const [showCoinsGate, setShowCoinsGate] = useState(false)
  const [coinsGateRequired, setCoinsGateRequired] = useState(COINS_POR_CALCULO)
  const [showCalculoConfirm, setShowCalculoConfirm] = useState(false)
  const [showNuevoCasoConfirm, setShowNuevoCasoConfirm] = useState(false)
  const [showReiniciarConfirm, setShowReiniciarConfirm] = useState(false)
  const [tasaInteresTipo, setTasaInteresTipo] = useState('PASIVA_BCRA_A14290')
  const [jurisdiccion, setJurisdiccion] = useState('ANSeS_NACIONAL')
  const [dataTablePage, setDataTablePage] = useState(1)
  const [mostrarViabilidad, setMostrarViabilidad] = useState(false)
  const [calculoFetchError, setCalculoFetchError] = useState('')
  const [showAllDetalle, setShowAllDetalle] = useState(false)
  const [ultimoPeriodoDetectado, setUltimoPeriodoDetectado] = useState('')
  const [fechaCeseEstimada, setFechaCeseEstimada] = useState('')
  const [showTareasCuidadoModal, setShowTareasCuidadoModal] = useState(false)
  // rowsEditadas + setRowsEditadas + addRevisionRow + removeRevisionRow → desde useCaseData (Phase 3)
  const [cargaHistorialModo, setCargaHistorialModo] = useState('')
  const [manualRowsDraft, setManualRowsDraft] = useState([
    { periodo: '', empresa: '', cuit: '', monto: '', estrategia: 'EXCLUIR_DIVISOR' },
  ])
  const [rawRows, setRawRows] = useState([])
  const [expandedYears, setExpandedYears] = useState({})
  const [activeInfoTab, setActiveInfoTab] = useState('basica')
  const [calculoReady, setCalculoReady] = useState(false)
  const [calculoLoading, setCalculoLoading] = useState(false)
  const [titularCuilOrigen, setTitularCuilOrigen] = useState('')
  const [actividadFuentes, setActividadFuentes] = useState({ autonomos: [], desempleo: [] })
  const [fechaCeseCabeceraPdf, setFechaCeseCabeceraPdf] = useState('')
  const [fechaCeseSugerida, setFechaCeseSugerida] = useState('')
  const [manualResumenTotalesDraft, setManualResumenTotalesDraft] = useState([{ cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
  const [reviewTab, setReviewTab] = useState('edicion')
  const [reviewMode, setReviewMode] = useState('simple')
  const [selectedConflictCuit, setSelectedConflictCuit] = useState('')
  const [modoHerramienta, setModoHerramienta] = useState('base')
  const [normativeVersion, setNormativeVersion] = useState('N/D')
  const [normativeAudit, setNormativeAudit] = useState([])
  const [colorProfile, setColorProfile] = useState(DEFAULT_PROFILE)
  const [showManualRangePanel, setShowManualRangePanel] = useState(false)
  const [manualRangeDraft, setManualRangeDraft] = useState({ desde: '', hasta: '', empleador: '', cuit: '', forzarAgregar: false })

  const caseId = useMemo(() => {
    const cuitKey = String(expediente?.cuilCuit || '').replace(/\D/g, '') || 'sin-cuit'
    const fechaKey = String(expediente?.fechaCese || '').slice(0, 10) || 'sin-cese'
    return `${cuitKey}-${fechaKey}`
  }, [expediente?.cuilCuit, expediente?.fechaCese])

  const mutations = useMutationAudit({
    userToken: auth.userToken,
    caseId,
    expediente,
  })
  const {
    mutationsLog, setMutationsLog,
    showMutationsAccordion, setShowMutationsAccordion,
    showMutationReasonModal, setShowMutationReasonModal,
    pendingMutationAction, setPendingMutationAction,
    mutationReasonDraft, setMutationReasonDraft,
    mutationsIntegrityHash, setMutationsIntegrityHash,
    copyHashFeedback, setCopyHashFeedback,
    allowLitigioArtesanalBypass, setAllowLitigioArtesanalBypass,
    mutationsQualityAudit, mutationReasonWeak,
    openMutationReasonModal, commitMutationAction,
    registrarBypassLitigioArtesanal, resetMutations, syncCaseAudit,
  } = mutations

  // ── onMutationLog: appends an entry to mutationsLog (pure updater) ──
  const onMutationLog = useCallback((entry) => {
    setMutationsLog((prev) => [...prev, entry])
  }, [setMutationsLog])
  // Sync to server via a separate effect (avoids side-effects inside updater).
  // Use a ref for syncCaseAudit so the effect only fires when mutationsLog changes,
  // not when expediente (which syncCaseAudit depends on) changes.
  const syncCaseAuditRef = useRef(syncCaseAudit)
  useEffect(() => { syncCaseAuditRef.current = syncCaseAudit }, [syncCaseAudit])
  const prevMutationsLogRef = useRef(mutationsLog)
  useEffect(() => {
    if (prevMutationsLogRef.current === mutationsLog) return
    prevMutationsLogRef.current = mutationsLog
    if (!mutationsLog.length) return // skip initial load / resets
    // Sync the full log (the ref ensures latest syncCaseAudit with correct expediente)
    syncCaseAuditRef.current(mutationsLog)
  }, [mutationsLog])

  const [caseResetNotice, setCaseResetNotice] = useState('')
  const [lastLocalSaveAt, setLastLocalSaveAt] = useState('')
  const [prorrateoServiciosDraft, setProrrateoServiciosDraft] = useState([
    { regimen: 'GENERAL', anios: '' },
    { regimen: 'INSALUBRE', anios: '' },
  ])
  const {
    simulationEnabled,
    simulationCases,
    selectedCaseId,
    setSelectedCaseId,
    selectedCase,
  } = useSimulationMode()
  const fingerprintCheckedRef = useRef(false)

  const coins = useCoins(auth.userRef)
  const tickets = useTickets(auth.authUid, auth.isUserAuthenticated)
  const norm = useNormative()
  const caseData = useCaseData({
    onRowsMutated: () => setCalculoReady(false),
    onMutationLog,
    actividadFuentes,
    fechaCeseCabeceraPdf,
  })
  const { rowsEditadas, setRowsEditadas, addRevisionRow, removeRevisionRow, updateRowField } = caseData

  // ── updateRowFieldWithAudit: wraps updateRowField with mutation audit ──
  const updateRowFieldWithAudit = useCallback((rowId, field, value, meta) => {
    // If meta is already provided (e.g. from manual range panel), pass through
    if (meta?.reasonCode) {
      updateRowField(rowId, field, value, meta)
      return
    }

    const row = rows.find((r) => String(r.rowId) === String(rowId))
    if (!row) {
      updateRowField(rowId, field, value)
      return
    }

    const originalValue = row[field]
    const isCritical =
      field === 'periodo' ||
      field === 'cuit' ||
      (['monto', 'montoDeclarado'].includes(field) &&
        Math.abs(Number(value) - Number(originalValue || 0)) > 5000)

    if (isCritical) {
      // Show modal — user must provide reason; commitMutationAction handles logging
      openMutationReasonModal({
        rowId,
        periodo: row.periodoKey || row.periodo || '',
        field,
        originalValue,
        newValue: value,
        apply: () => {
          updateRowField(rowId, field, value)
        },
        sourcePage: 4,
      })
    } else {
      // Auto-log with default reason
      updateRowField(rowId, field, value, {
        reasonCode: 'ajuste_manual_perito',
        reasonDetail: `Ajuste manual de ${field}`,
      })
    }
  }, [rows, updateRowField, openMutationReasonModal])

  // ── Phase 2 hook calls ──
  const { indicesMovilidad, indicesLoading, error: hookIndicesError } = useIndices()
  const { config: normativeConfig, loading: normativeConfigLoading } = useNormativeConfig()
  useEffect(() => {
    window.__seedNormativeConfig = seedNormativeConfig
    return () => { delete window.__seedNormativeConfig }
  }, [])
  const [, setIndicesMovilidad] = useState(null)

  /**
   * Resetea el estado del caso que NO está en useCaseData.
   * Se llama junto con caseData.resetCoreCaseSession() para
   * limpiar todo el estado del caso actual antes de cargar
   * uno nuevo (loadManualRows, Dropzone onProcessed).
   */
  const resetRemainingSession = () => {
    setActividadFuentes({ autonomos: [], desempleo: [] })
    setFechaCeseCabeceraPdf('')
    setUltimoPeriodoDetectado('')
    setFechaCeseSugerida('')
    resetProcessingSession()
    setManualResumenTotalesDraft([{ cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
    setSelectedConflictCuit('')
    setCalculoReady(false)
  }

  const toMillis = (value) => {
    if (value?.toDate) return value.toDate().getTime()
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? 0 : d.getTime()
  }

  const MIN_REASON_DETAIL_LEN = 20

  const formatArs = (value) => `$${Math.round(Number(value || 0)).toLocaleString('es-AR')}`

  const sanitizeExpedienteScenarioForPdfIngestion = (base) => ({
    ...(base || {}),
    esDocente: false,
    tieneBeneficioPrevio: false,
    datosDocentes: {
      cargoCese: '',
      sueldoBrutoCargoCese: '',
      mesesEnCargoContinuos: 0,
      mesesEnCargoAlternados: 0,
    },
    datosPension: {
      tipoPension: '',
      fechaFallecimiento: '',
      haberJubiladoCausante: '',
      concurreConyugeConviviente: true,
      concurrentes: { conyuge: true, hijosCount: 0 },
    },
  })

  const clearCaseSessionStorage = () => {
    try {
      localStorage.removeItem(EXPEDIENTE_STORAGE_KEY)
      localStorage.removeItem(CASE_FINGERPRINT_KEY)
      const toDelete = []
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = String(localStorage.key(i) || '')
        if (key.startsWith(INDICES_CACHE_PREFIX)) toDelete.push(key)
      }
      toDelete.forEach((k) => localStorage.removeItem(k))
    } catch {
      // ignore
    }
  }

  const resetCaseSession = ({ preservePersonals = true, reason = 'Sesión técnica reiniciada.' } = {}) => {
    const prev = expediente || createBaseExpediente()
    const base = createBaseExpediente()
    const nextExpediente = preservePersonals
      ? {
        ...base,
        nombre: prev.nombre || '',
        apellido: prev.apellido || '',
        cuilCuit: prev.cuilCuit || '',
        fechaNacimiento: prev.fechaNacimiento || '',
        sexo: prev.sexo || 'F',
      }
      : base

    setExpediente(nextExpediente)
    setRows([])
    setRawRows([])
    setRowsEditadas(null)
    setActividadFuentes({ autonomos: [], desempleo: [] })
    setTitularCuilOrigen('')
    setProcessingPayload(null)
    setProcessingPayloadAt('')
    setUltimoPeriodoDetectado('')
    setFechaCeseCabeceraPdf('')
    setFechaCeseSugerida('')
    setShowManualRangePanel(false)
    setManualRangeDraft({ desde: '', hasta: '', empleador: '', cuit: '', forzarAgregar: false })
    setManualRowsDraft([{ periodo: '', empresa: '', cuit: '', monto: '' }])
    setExpandedYears({})
    setReviewTab('edicion')
    setReviewMode('simple')
    setSelectedConflictCuit('')
    resetMutations()
    setCalculoReady(false)
    setCalculoLoading(false)
    setDictamenText('')
    setDictamenVerificationCode('')
    setShowDictamen(false)
    setCaseResetNotice(reason)
    setFechaCeseEstimada('')
    setManualResumenTotalesDraft([{ cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
    setProrrateoServiciosDraft([
      { regimen: 'GENERAL', anios: '' },
      { regimen: 'INSALUBRE', anios: '' },
    ])
    setShadowResumenTotales(new Map())
    setShadowResumenMeta({ sourceConfidence: 1, qualityWarning: '', duplicateRatio: 0 })
    setConciliacionManualActa(null)
    setActiveInfoTab('basica')
    goToStep(0)
    setModoHerramienta(modoHerramienta)
    clearCaseSessionStorage()
  }

  useEffect(() => {
    const key = auth.userRef ? `colorProfile:${auth.userRef}` : 'colorProfile:anon'
    const saved = String(localStorage.getItem(key) || '').toLowerCase()
    setColorProfile(COLOR_PROFILES[saved] ? saved : DEFAULT_PROFILE)
  }, [auth.userRef])

  useEffect(() => {
    if (fingerprintCheckedRef.current) return
    fingerprintCheckedRef.current = true
    try {
      const stored = JSON.parse(localStorage.getItem(CASE_FINGERPRINT_KEY) || '{}')
      const current = {
        caseId,
        indiceVersion: String(indicesMovilidad?.__canonical?.version || 'N/D'),
        normativeVersion: String(normativeVersion || 'N/D'),
      }
      const hasCaseData = rows.length > 0 || rawRows.length > 0 || mutationsLog.length > 0
      if (hasCaseData && stored?.caseId && stored.caseId !== current.caseId) {
        resetCaseSession({ preservePersonals: true, reason: `Sesión técnica reiniciada por cambio de caso (${stored.caseId} -> ${current.caseId}).` })
      }
    } catch {
      // ignore
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      const fp = {
        caseId,
        indiceVersion: String(indicesMovilidad?.__canonical?.version || 'N/D'),
        normativeVersion: String(normativeVersion || 'N/D'),
        updatedAt: new Date().toISOString(),
      }
      localStorage.setItem(CASE_FINGERPRINT_KEY, JSON.stringify(fp))
    } catch {
      // ignore
    }
  }, [caseId, indicesMovilidad?.__canonical?.version, normativeVersion])

  useEffect(() => {
    setAllowLitigioArtesanalBypass(false)
  }, [caseId])

  useEffect(() => {
    if (!caseResetNotice) return
    const t = setTimeout(() => setCaseResetNotice(''), 7000)
    return () => clearTimeout(t)
  }, [caseResetNotice])

  useEffect(() => {
    try {
      localStorage.setItem(EXPEDIENTE_STORAGE_KEY, JSON.stringify(expediente))
      setLastLocalSaveAt(new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
    } catch {
      // ignore
    }
  }, [expediente])

  useEffect(() => {
    const root = document.documentElement
    const body = document.body
    root.classList.remove('theme-light', 'theme-dark')
    body.classList.remove('theme-light', 'theme-dark')
    const profile = COLOR_PROFILES[colorProfile] || COLOR_PROFILES[DEFAULT_PROFILE]
    const themeClass = profile.isDark ? 'theme-dark' : 'theme-light'
    root.classList.add(themeClass)
    body.classList.add(themeClass)
    root.setAttribute('data-color-profile', colorProfile)
    body.setAttribute('data-color-profile', colorProfile)
    Object.entries(profile.vars).forEach(([k, v]) => root.style.setProperty(k, v))
  }, [colorProfile])

  useEffect(() => {
    if (!engineReady || typeof getIndices !== 'function') return
    const controller = new AbortController()
    ;(async () => {
      try {
        const response = await fetch('/api/normative/audit?limit=5', { signal: controller.signal })
        if (!response.ok) return
        const data = await response.json()
        setNormativeAudit(Array.isArray(data?.data) ? data.data : [])
      } catch (err) {
        if (err?.name === 'AbortError') return
        setNormativeAudit([])
      }
    })()
    return () => { controller.abort() }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        const response = await fetch('/api/normative/rules/active', { signal: controller.signal })
        if (!response.ok) return
        const data = await response.json()
        const version = data?.data?.version || data?.data?.id || 'N/D'
        setNormativeVersion(version)
      } catch (err) {
        if (err?.name === 'AbortError') return
        setNormativeVersion('N/D')
      }
    })()
    return () => { controller.abort() }
  }, [])

  useEffect(() => {
    if (!auth.isUserAuthenticated) {
      setOpenTickets(0)
      return undefined
    }
    const uid = auth.authUid
    if (!uid) {
      setOpenTickets(0)
      setHasAdminUpdates(false)
      return undefined
    }
    const q = query(
      collection(db, 'tickets'),
      where('userUid', '==', uid),
      limit(20)
    )
    const unsub = onSnapshot(
      q,
      (snap) => {
        const tickets = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        const abiertos = tickets.filter((ticket) => ticket.estado !== 'resuelto').length
        const alertasAdmin = tickets.some((ticket) => ticket.estado !== 'resuelto' && ticket.lastMessageRole === 'admin' && toMillis(ticket.lastMessageAt || ticket.updatedAt) > ticketsSeenAt)
        setOpenTickets(abiertos)
        setHasAdminUpdates(alertasAdmin)
      },
      () => {
        setOpenTickets(0)
        setHasAdminUpdates(false)
      }
    )
    return () => unsub()
  }, [auth.isUserAuthenticated, auth.userRef, auth.authUid, ticketsSeenAt])

  useEffect(() => {
    const picked = JURISDICCIONES_DISPONIBLES.find((j) => j.value === jurisdiccion)
    if (picked?.tasaDefault && picked.tasaDefault !== tasaInteresTipo) {
      setTasaInteresTipo(picked.tasaDefault)
    }
  }, [jurisdiccion, engineReady])

  useEffect(() => {
    if (modoHerramienta === 'base') {
      if (jurisdiccion !== 'ANSeS_NACIONAL') setJurisdiccion('ANSeS_NACIONAL')
      if (expediente.fallo !== 'IPC') {
        setExpediente((p) => ({ ...p, fallo: 'IPC' }))
      }
    }
  }, [modoHerramienta, expediente.fallo, jurisdiccion])

  const openTicketsCenter = () => {
    const now = Date.now()
    setTicketsSeenAt(now)
    localStorage.setItem('ticketsSeenAt', String(now))
    setHasAdminUpdates(false)
    setShowTicketsModal(true)
  }

  const onChangeProfile = (next) => {
    const val = COLOR_PROFILES[next] ? next : DEFAULT_PROFILE
    const key = auth.userRef ? `colorProfile:${auth.userRef}` : 'colorProfile:anon'
    setColorProfile(val)
    localStorage.setItem(key, val)
  }

  const themeToggle = (
    <div className="panel-profile fixed bottom-3 right-3 z-50 w-[min(92vw,280px)] rounded-xl p-2.5 text-xs font-semibold shadow-lg backdrop-blur md:bottom-4 md:right-4 md:w-72">
      <p className="text-profile-secondary mb-1 text-[10px] uppercase tracking-wide">Perfil visual</p>
      <select
        value={colorProfile}
        onChange={(e) => onChangeProfile(e.target.value)}
        className="h-9 w-full rounded-lg border border-white/30 bg-slate-900/80 px-2 py-1 text-xs"
      >
        {Object.entries(COLOR_PROFILES).map(([id, cfg]) => <option key={id} value={id}>{cfg.label}</option>)}
      </select>
    </div>
  )

  const onGenerarDictamen = async () => {
    if (Number(coins.coinsBalance || 0) < 1) {
      setShowCoinsGate(true)
      return
    }
    setShowDictamen(true)
    setDictamenLoading(true)
    try {
      if (modoHerramienta === 'base') {
        const consumeResult = await coins.consumeCoins(1, 'dictamen_base')
        if (!consumeResult.success) {
          setDictamenText(`No se pudo generar el dictamen: ${consumeResult.error || 'saldo insuficiente o no autorizado'}`)
          setDictamenVerificationCode('')
          return
        }
        const r = contextoDictamen?.resumenEjecutivo || {}
        const titular = `${expediente.apellido || ''} ${expediente.nombre || ''}`.trim() || 'Titular'
        const textoBase = [
          'INFORME DE CALCULO PREVISIONAL BASE (SIN FALLOS)',
          `Titular: ${titular}`,
          `Fecha de cese: ${expediente.fechaCese || 'N/A'}`,
          '',
          'Resultado estimado:',
          `- Haber actual ANSES: $${Number(r.haberActualAnses || 0).toLocaleString('es-AR')}`,
          `- Haber estimado base: $${Number(r.haberBolsilloReajustado || 0).toLocaleString('es-AR')}`,
          `- Diferencia mensual estimada: $${Number(r.diferencialMensual || 0).toLocaleString('es-AR')}`,
          `- Incremento porcentual estimado: ${Number(r.incrementoMensualPct || 0).toFixed(2)}%`,
          `- Retroactivo tecnico estimado: $${Number(r.retroactivoTotal || 0).toLocaleString('es-AR')}`,
          '',
          'Alcance del documento:',
          '- Este informe representa una estimacion tecnica base sin aplicacion de fallos judiciales.',
          '- La estrategia litigiosa, doctrina y via procesal quedan a criterio del abogado interviniente.',
        ].join('\n')
        const localCode = `BASE-${Date.now().toString(36).toUpperCase()}`
        setDictamenText(textoBase)
        setDictamenVerificationCode(localCode)
      } else {
        const cuitDictamen = resolveCuitForEngine(expediente.cuilCuit).cuit
        const result = await fetchDictamen(contextoDictamen, auth.userRef || cuitDictamen || expediente.dni)
        setDictamenText(result.text)
        setDictamenVerificationCode(result.verificationCode)
      }
    } catch (e) {
      setDictamenText(`No se pudo generar el dictamen: ${e?.message || 'error desconocido'}`)
      setDictamenVerificationCode('')
    } finally {
      setDictamenLoading(false)
    }
  }

  const updateManualRow = (index, field, value) => {
    setManualRowsDraft((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const addManualRow = () => {
    setManualRowsDraft((prev) => [...prev, { periodo: '', empresa: '', cuit: '', monto: '', estrategia: 'EXCLUIR_DIVISOR' }])
  }

  const handleNuevoCaso = () => {
    setShowNuevoCasoConfirm(true)
  }

  const handleNuevoCasoConfirm = () => {
    setShowNuevoCasoConfirm(false)
    resetCaseSession({
      preservePersonals: true,
      reason: 'Sesión técnica reiniciada manualmente: nuevo caso.',
    })
  }

  const handleReiniciar = () => {
    setShowReiniciarConfirm(true)
  }

  const handleReiniciarConfirm = () => {
    setShowReiniciarConfirm(false)

    // 1. Reset hook interno (expediente, rows del hook + localStorage 'expedienteData')
    caseData.resetCoreCaseSession()

    // 2. Reset completo de App.jsx (~26 state vars + localStorage 'motor_previsional_expediente')
    resetCaseSession({
      preservePersonals: false,
      reason: 'Caso reiniciado por el usuario.',
    })

    // 3. Resetear variables que resetCaseSession no cubre
    setModoHerramienta('base')
    setCargaHistorialModo('')
    setJurisdiccion('ANSeS_NACIONAL')
    setTasaInteresTipo('PASIVA_BCRA_A14290')
    setCalculoFetchError('')
    setDictamenLoading(false)
    setDataTablePage(1)
    setMostrarViabilidad(false)
    setShowCalculoConfirm(false)
    setShowCoinsGate(false)
    setProgressLabel('')

    // 4. Limpiar storage stale
    try {
      sessionStorage.removeItem('wizardFormStep')
      const keysToRemove = []
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = String(sessionStorage.key(i) || '')
        if (key.startsWith('sesionPagada_') || key === 'currentTransactionId') {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k))
    } catch { /* ignore */ }

    // 5. Navegar al paso 0 (selector de modo) — goToStep(0) no funciona (clamps a 1)
    setFormStep(0)
  }

  const updateProrrateoServicio = (index, field, value) => {
    setProrrateoServiciosDraft((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const addProrrateoServicio = () => {
    setProrrateoServiciosDraft((prev) => [...prev, { regimen: 'PROVINCIAL', anios: '' }])
  }

  const applyProrrateoServicios = () => {
    const clean = prorrateoServiciosDraft
      .map((r) => ({ regimen: String(r?.regimen || 'GENERAL'), anios: Number(r?.anios || 0) }))
      .filter((r) => r.anios > 0)
    setExpediente((p) => ({ ...p, prorrateoServicios: clean }))
  }

  const updateManualResumenRow = (index, field, value) => {
    setManualResumenTotalesDraft((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const addManualResumenRow = () => {
    setManualResumenTotalesDraft((prev) => [...prev, { cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
  }

  const sanitizeCuit = (value) => String(value || '').replace(/\D/g, '')

  const validarCuitModulo11 = (value) => {
    const digits = sanitizeCuit(value)
    if (!/^\d{11}$/.test(digits)) return false
    const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
    const sum = weights.reduce((acc, w, i) => acc + Number(digits[i]) * w, 0)
    const mod = 11 - (sum % 11)
    const check = mod === 11 ? 0 : (mod === 10 ? 9 : mod)
    return check === Number(digits[10])
  }

  const resolveCuitForEngine = (value) => {
    const digits = sanitizeCuit(value)
    if (validarCuitModulo11(digits)) {
      return { cuit: digits, fallbackApplied: false }
    }
    return { cuit: '20000000000', fallbackApplied: true }
  }



  // ── useProcessingPayload (replaces inline 5 useState + 6 useMemo + useSummaryShadow) ──
  const {
    processingPayload, setProcessingPayload,
    processingPayloadAt, setProcessingPayloadAt,
    shadowResumenTotales, setShadowResumenTotales,
    shadowResumenMeta, setShadowResumenMeta,
    conciliacionManualActa, setConciliacionManualActa,
    processParserOutput, applyManualResumenTotales, resetProcessingSession,
    shadowBilling, scoreCertezaInforme, shadowTopConflicts,
    periodosResumenControl, resumenHistorialLaboralRows, simulationFlowActive,
  } = useProcessingPayload({
    rows, expediente, CORE_RULES_CONFIG,
    computeShadowBilling, extractResumenTotalesPorCuit,
    computeActaHash, normalizeReviewRows, sanitizarRemuneraciones,
  })

  const loadSimulationCase = () => {
    if (!simulationEnabled || !selectedCase) return
    applySimulationPayload(selectedCase, {
      setUltimoPeriodoDetectado,
      setFechaCeseCabeceraPdf,
      setFechaCeseSugerida,
      setActividadFuentes,
      setTitularCuilOrigen,
      setProcessingPayload,
      setProcessingPayloadAt,
      setRawRows,
      normalizeReviewRows,
      sanitizarRemuneraciones,
      activeRules,
      setRows,
      setCalculoReady,
      setCargaHistorialModo,
      setShadowResumenTotales,
      setShadowResumenMeta,
      setManualResumenTotalesDraft,
      setConciliacionManualActa,
      setExpediente,
      setModoHerramienta,
      setNormativeVersion,
      setIndicesMovilidad,
    })
  }



  const minusMonthsIso = (value, months) => {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    d.setMonth(d.getMonth() - Number(months || 0))
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
  }

  const rowsTrazabilidadInterna = useMemo(() => buildMissingRows(rows, { visualOnly: false }, { actividadFuentes, expedienteFechaCese: expediente.fechaCese, fechaCeseCabeceraPdf }), [rows, actividadFuentes, expediente.fechaCese, fechaCeseCabeceraPdf])

  const loadManualRows = () => {
    caseData.resetCoreCaseSession()
    resetRemainingSession()
    const draft = Array.isArray(manualRowsDraft) ? manualRowsDraft : []
    const fallbackSmvm = Number(activeRules?.economia?.haberMinimoFallback || 210000)
    const withStrategy = draft.map((r) => {
      const periodo = String(r.periodo || '').trim()
      const montoRaw = parseMontoValue(r.monto || 0)
      if (!periodo) return null
      if (montoRaw > 0) return { ...r, montoResolved: montoRaw, incluirEnPromedio: true, estrategiaMontoFaltante: 'NOMINAL' }
      const estrategia = r.estrategia || 'EXCLUIR_DIVISOR'
      if (estrategia === 'SMVM_HISTORICO') return { ...r, montoResolved: fallbackSmvm, incluirEnPromedio: true, estrategiaMontoFaltante: 'SMVM_HISTORICO' }
      if (estrategia === 'MANTENER_CERO') return { ...r, montoResolved: 0, incluirEnPromedio: true, estrategiaMontoFaltante: 'MANTENER_CERO' }
      return { ...r, montoResolved: 0, incluirEnPromedio: false, estrategiaMontoFaltante: 'EXCLUIR_DIVISOR' }
    }).filter(Boolean)
    const mapped = manualRowsDraft
      .map((r, idx) => withStrategy[idx])
      .filter((r) => String(r?.periodo || '').trim())
      .map((r) => ({
        periodoISO: String(r.periodo),
        periodo: `${String(r.periodo).slice(5, 7)}/${String(r.periodo).slice(0, 4)}`,
        empresa: r.empresa,
        razonSocial: r.empresa,
        cuit: String(r.cuit || '').trim(),
        monto: Number(r.montoResolved || 0),
        remImpSS: Number(r.montoResolved || 0),
        montoOriginal: Number(r.montoResolved || 0),
        incluirEnPromedio: r.incluirEnPromedio !== false,
        estrategiaMontoFaltante: r.estrategiaMontoFaltante || 'NOMINAL',
        tipo: 'Dependencia',
      }))
    setRawRows(normalizeReviewRows(mapped))
    setActividadFuentes({ autonomos: [], desempleo: [] })
    setFechaCeseCabeceraPdf('')
    const limpias = sanitizarRemuneraciones(mapped, expediente.fechaCese, ultimoPeriodoDetectado || '', activeRules)
    const normalized = normalizeReviewRows(limpias)
    resetProcessingSession()
    setManualResumenTotalesDraft([{ cuit: '', total: '', motivo: '', evidenciaFuente: '' }])
    setRows(normalized)
    setRawRows(normalized.map((r) => ({ ...r, _raw: true })))
    setCalculoReady(false)
  }

  const onGenerarCalculo = async () => {
    if (calculoLoading) return
    setCalculoLoading(true)
    try {
      if (!rows.length) return
      if (Number(coins.coinsBalance || 0) < 2) {
        setShowCoinsGate(true)
        return
      }
      const consumeResult = await coins.consumeCoins(2, 'calculo')
      if (!consumeResult.success) {
        setShowCoinsGate(true)
        return
      }
      setCalculoReady(true)
      goToStep(5)
    } catch (err) {
      setCalculoFetchError(err?.message || 'Error de conexión al generar cálculo. Intentalo de nuevo.')
    } finally {
      setCalculoLoading(false)
    }
  }

  const groupedByYear = useMemo(() => {
    const groups = {}
    const baseRows = reviewMode === 'perito' ? buildMissingRows(rows, {}, { actividadFuentes, expedienteFechaCese: expediente.fechaCese, fechaCeseCabeceraPdf }) : normalizeReviewRows(rows)
    baseRows.forEach((r, idx) => {
      const key = String(r.periodoKey || '')
      const year = key.slice(0, 4) || 'S/D'
      if (!groups[year]) groups[year] = []
      groups[year].push({ ...r, _idx: idx, _rowId: String(r.rowId || '') })
    })
    return Object.entries(groups)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([year, items]) => [year, items.slice().sort((a, b) => String(b.periodoKey || '').localeCompare(String(a.periodoKey || '')))])
  }, [rows, reviewMode, actividadFuentes, expediente.fechaCese, fechaCeseCabeceraPdf])

  const groupedByYearFiltered = useMemo(() => {
    if (!selectedConflictCuit) return groupedByYear
    return groupedByYear
      .map(([year, items]) => [year, items.filter((r) => String(r.cuit || '').replace(/\D/g, '') === selectedConflictCuit)])
      .filter(([, items]) => items.length > 0)
  }, [groupedByYear, selectedConflictCuit])

  const mirrorRows = useMemo(() => {
    const sys = buildMissingRows(rows, {}, { actividadFuentes, expedienteFechaCese: expediente.fechaCese, fechaCeseCabeceraPdf })
    const rawNormalized = normalizeReviewRows(rawRows)
    const rawByKey = new Map()
    rawNormalized.forEach((r) => {
      const k = String(r.periodoKey || '')
      if (!rawByKey.has(k)) rawByKey.set(k, r)
    })
    return sys.map((r) => {
      const raw = rawByKey.get(String(r.periodoKey || '')) || null
      return {
        periodoKey: r.periodoKey,
        raw,
        system: r,
        changedEmpresa: raw ? String(raw.empresa || '').trim() !== String(r.empresa || '').trim() : false,
        changedCuit: raw ? String(raw.cuit || '').trim() !== String(r.cuit || '').trim() : false,
        changedMonto: raw ? Number(raw.monto || 0) !== Number(r.monto || 0) : false,
      }
    })
  }, [rows, rawRows, actividadFuentes, expediente.fechaCese, fechaCeseCabeceraPdf])

  const downloadProcessingLog = () => {
    const payload = {
      generatedAt: new Date().toISOString(),
      uploadedAt: processingPayloadAt || null,
      governanceFlags: {
        modoCaso: modoCaso.key,
        tieneBeneficioPrevio: Boolean(expediente?.tieneBeneficioPrevio),
        modoPlanificacion: Boolean(expedienteCalculo?.modoPlanificacion),
        cuilCuitSanitizado: String(expedienteCalculo?.cuilCuitSanitizado || ''),
        cuilCuitFallbackApplied: Boolean(expedienteCalculo?.cuilCuitFallbackApplied),
        cuilCuitEngine: String(expedienteCalculo?.cuilCuit || ''),
        haberActualFuente: expedienteCalculo?.haberActualFallbackApplied ? 'fallback_minimo_legal' : 'usuario',
        haberActualAplicado: Number(expedienteCalculo?.haberActual || 0),
        fechaPrescripcionCalculo: String(expedienteCalculo?.fechaPrescripcionCalculo || ''),
        prescripcionModo: String(expediente?.fechaReclamoAdministrativo || '').trim() ? 'hitos_expediente' : 'fallback_fecha_corrida',
      },
      scoreCertezaInforme,
      shadowBilling,
      shadowResumenMeta,
      shadowResumenTotales: Object.fromEntries(Array.from(shadowResumenTotales.entries())),
      conciliacionManualActa,
      rowsCount: Array.isArray(rows) ? rows.length : 0,
      rawRowsCount: Array.isArray(rawRows) ? rawRows.length : 0,
      parserOutput: processingPayload,
      mirrorRows,
      rowsSanitized: rows,
      manualMissingAmountStrategies: (Array.isArray(rows) ? rows : [])
        .filter((r) => String(r?.estrategiaMontoFaltante || '').trim())
        .map((r) => ({
          periodoKey: String(r?.periodoKey || ''),
          estrategiaMontoFaltante: String(r?.estrategiaMontoFaltante || ''),
          incluirEnPromedio: r?.incluirEnPromedio !== false,
          monto: Number(r?.monto || 0),
        })),
      rowsRaw: rawRows,
    }
    const fileBody = `${JSON.stringify(payload, null, 2)}\n`
    const blob = new Blob([fileBody], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const stamp = new Date().toISOString().replace(/[:.]/g, '-')
    const a = document.createElement('a')
    a.href = url
    a.download = `procesamiento-${stamp}.log`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const applyRowFieldUpdate = (rowId, field, value) => {
    const isSacMonth = (key) => {
      const txt = String(key || '')
      return txt.endsWith('-06') || txt.endsWith('-12')
    }
    const computeRowWithSac = (row) => {
      const declarado = Number(row.montoDeclarado ?? row.monto ?? row.remImpSS ?? 0)
      const incluyeSac = Boolean(row.incluyeSac)
      const aplicaSac = incluyeSac && isSacMonth(row.periodoKey)
      const sacManual = Number(row.sacManual || 0)
      const sacMonto = aplicaSac ? (sacManual > 0 ? sacManual : declarado / 12) : 0
      const computable = Math.max(0, declarado - sacMonto)
      return {
        ...row,
        montoDeclarado: Number(declarado.toFixed(2)),
        sacMonto: Number(sacMonto.toFixed(2)),
        sacAplicado: aplicaSac,
        monto: Number(computable.toFixed(2)),
        remImpSS: Number(computable.toFixed(2)),
        montoOriginal: Number(computable.toFixed(2)),
      }
    }
    setRows((prev) => {
      const targetId = String(rowId || '')
      const hasTarget = prev.some((r) => String(r.rowId || '') === targetId)
      const withTarget = hasTarget
        ? prev
        : (() => {
            const virtual = buildMissingRows(prev, {}, { actividadFuentes, expedienteFechaCese: expediente.fechaCese, fechaCeseCabeceraPdf }).find((r) => String(r.rowId || '') === targetId)
            if (!virtual) return prev
            return [...prev, {
              ...virtual,
              rowId: targetId,
              manualAdded: true,
              manualEdited: true,
              missingInSource: true,
            }]
          })()

      return normalizeReviewRows(withTarget.map((r) => {
        if (String(r.rowId || '') !== targetId) return r
      if (field === 'periodo') {
        const raw = String(value || '')
        const iso = /^\d{4}-\d{2}$/.test(raw) ? raw : String(r.periodoISO || r.periodoKey || '')
        const human = /^\d{4}-\d{2}$/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : String(r.periodo || '')
        return computeRowWithSac({ ...r, periodo: human, periodoISO: iso, periodoKey: iso })
      }
      if (field === 'monto' || field === 'montoDeclarado') {
        const n = parseMontoValue(value)
        return computeRowWithSac({ ...r, montoDeclarado: n })
      }
      if (field === 'incluyeSac') {
        return computeRowWithSac({ ...r, incluyeSac: Boolean(value) })
      }
      if (field === 'sacManual') {
        return computeRowWithSac({ ...r, sacManual: parseMontoValue(value) })
      }
      return computeRowWithSac({ ...r, [field]: value })
      }))
    })
    setCalculoReady(false)
  }

  // addRevisionRow + removeRevisionRow → desde useCaseData (Phase 3)

  const parseManualRangeDate = (value) => {
    const txt = String(value || '').trim()
    const m = txt.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (!m) return null
    const day = Number(m[1])
    const month = Number(m[2])
    const year = Number(m[3])
    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return null
    if (month < 1 || month > 12 || year < 1900 || year > 2100) return null
    const maxDay = new Date(year, month, 0).getDate()
    if (day < 1 || day > maxDay) return null
    return { day, month, year }
  }

  const toIsoDate = ({ year, month, day }) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  const buildManualRangeMonths = (desdeParts, hastaParts) => {
    const out = []
    let y = desdeParts.year
    let m = desdeParts.month
    const endY = hastaParts.year
    const endM = hastaParts.month
    while (y < endY || (y === endY && m <= endM)) {
      const isStartMonth = y === desdeParts.year && m === desdeParts.month
      const isEndMonth = y === hastaParts.year && m === hastaParts.month
      let dias = 30
      if (isStartMonth && isEndMonth) {
        dias = Math.max(1, hastaParts.day - desdeParts.day + 1)
      } else if (isStartMonth) {
        dias = Math.max(1, 30 - desdeParts.day)
      } else if (isEndMonth) {
        dias = Math.max(1, hastaParts.day)
      }
      out.push({
        key: `${y}-${String(m).padStart(2, '0')}`,
        dias,
        fechaDesde: isStartMonth ? toIsoDate(desdeParts) : `${y}-${String(m).padStart(2, '0')}-01`,
        fechaHasta: isEndMonth ? toIsoDate(hastaParts) : `${y}-${String(m).padStart(2, '0')}-30`,
      })
      if (m === 12) {
        y += 1
        m = 1
      } else {
        m += 1
      }
    }
    return out
  }

  const applyManualRange = () => {
    const desdeRaw = String(manualRangeDraft.desde || '')
    const hastaRaw = String(manualRangeDraft.hasta || '')
    const empleador = String(manualRangeDraft.empleador || '').trim()
    const cuitManual = String(manualRangeDraft.cuit || '').trim()
    const forzarAgregar = Boolean(manualRangeDraft.forzarAgregar)
    const desde = parseManualRangeDate(desdeRaw)
    const hasta = parseManualRangeDate(hastaRaw)
    if (!desde || !hasta) {
      alert('Completa desde/hasta en formato valido (DD/MM/AAAA).')
      return
    }
    const desdeSort = `${desde.year}-${String(desde.month).padStart(2, '0')}-${String(desde.day).padStart(2, '0')}`
    const hastaSort = `${hasta.year}-${String(hasta.month).padStart(2, '0')}-${String(hasta.day).padStart(2, '0')}`
    if (hastaSort < desdeSort) {
      alert('La fecha "hasta" no puede ser menor a "desde".')
      return
    }
    if (!empleador) {
      alert('Completa el empleador para aplicar la carga manual por rango.')
      return
    }
    const monthSlices = buildManualRangeMonths(desde, hasta)

    setRows((prev) => {
      const base = normalizeReviewRows(prev)
      const byKeyIndices = new Map()
      base.forEach((r, idx) => {
        const k = String(r.periodoKey || '')
        if (!k) return
        if (!byKeyIndices.has(k)) byKeyIndices.set(k, [])
        byKeyIndices.get(k).push(idx)
      })
      const nextRows = [...base]

      monthSlices.forEach((slice) => {
        const cursor = slice.key
        const existingIndices = byKeyIndices.get(cursor) || []
        if (existingIndices.length > 0 && !forzarAgregar) {
          existingIndices.forEach((idx) => {
            nextRows[idx] = {
              ...nextRows[idx],
              empresa: empleador,
              razonSocial: empleador,
              cuit: cuitManual || nextRows[idx].cuit || 'SIN_DATO',
              monto: 0,
              montoDeclarado: 0,
              remImpSS: 0,
              montoOriginal: 0,
              sacManual: 0,
              sacMonto: 0,
              sacAplicado: false,
              missingInSource: true,
              manualEdited: true,
              manualAdded: false,
              incluyeSac: false,
              diasOrigen: slice.dias,
              diasComputables: slice.dias,
              dias: slice.dias,
              fechaDesde: slice.fechaDesde,
              fechaHasta: slice.fechaHasta,
            }
          })
        } else {
          nextRows.push({
            periodoKey: cursor,
            periodoISO: cursor,
            periodo: `${cursor.slice(5, 7)}/${cursor.slice(0, 4)}`,
            empresa: empleador,
            razonSocial: empleador,
            cuit: cuitManual || 'SIN_DATO',
            monto: 0,
            montoDeclarado: 0,
            remImpSS: 0,
            montoOriginal: 0,
            missingInSource: true,
            manualAdded: true,
            incluyeSac: false,
            sacManual: 0,
            sacMonto: 0,
            sacAplicado: false,
            diasOrigen: slice.dias,
            diasComputables: slice.dias,
            dias: slice.dias,
            fechaDesde: slice.fechaDesde,
            fechaHasta: slice.fechaHasta,
          })
        }
      })
      return normalizeReviewRows(nextRows)
    })

    setCalculoReady(false)
    setShowManualRangePanel(false)
  }

  const empleadores = useMemo(() => new Set(rows.map((r) => r.cuit).filter(Boolean)).size, [rows])
  const rowsConProyeccion = useMemo(() => {
    const base = Array.isArray(rows) ? [...rows] : []
    if (!fechaCeseEstimada || !base.length) return base
    const maxKey = base.map((r) => String(r.periodoKey || r.periodoISO || '')).filter(Boolean).sort().pop()
    const endKey = String(fechaCeseEstimada || '').slice(0, 7)
    if (!maxKey || !endKey || endKey <= maxKey) return base

    const last = base.slice().sort((a, b) => String(a.periodoKey || a.periodoISO || '').localeCompare(String(b.periodoKey || b.periodoISO || ''))).pop()
    const ipcSerie = indicesMovilidad?.IPC?.serie || {}
    let currentKey = maxKey
    let currentAmount = Number(last?.monto || last?.remImpSS || last?.montoOriginal || 0)

    const projected = []
    while (currentKey < endKey) {
      const y = Number(currentKey.slice(0, 4))
      const m = Number(currentKey.slice(5, 7))
      const ny = m === 12 ? y + 1 : y
      const nm = m === 12 ? 1 : m + 1
      const nextKey = `${ny}-${String(nm).padStart(2, '0')}`

      const prevIpc = Number(ipcSerie[currentKey] || 0)
      const nextIpc = Number(ipcSerie[nextKey] || 0)
      const factor = prevIpc > 0 && nextIpc > 0 ? (nextIpc / prevIpc) : 1.02
      currentAmount = currentAmount * factor

      projected.push({
        ...last,
        periodoKey: nextKey,
        periodoISO: nextKey,
        periodo: `${nextKey.slice(5, 7)}/${nextKey.slice(0, 4)}`,
        monto: Number(currentAmount.toFixed(2)),
        remImpSS: Number(currentAmount.toFixed(2)),
        projected: true,
        requiresValidation: true,
      })
      currentKey = nextKey
      if (projected.length > 24) break
    }
    return [...base, ...projected]
  }, [rows, fechaCeseEstimada, indicesMovilidad])

  const normativeOverrides = useMemo(() => ({
    baseImponibleMaxima: normativeConfig?.topes,
    rentasReferencia: normativeConfig?.rentas,
  }), [normativeConfig])
  const rowsNormativa = useMemo(() => aplicarNormativaAnses(rowsConProyeccion, { ...expediente, ...normativeOverrides }), [rowsConProyeccion, expediente, normativeOverrides])
  const activeRules = useMemo(() => strategicEvaluator(expediente, rowsNormativa), [expediente, rowsNormativa])
  const expedienteCalculo = useMemo(() => ({ ...expediente, jurisdiccion }), [expediente, jurisdiccion])
  const rowsActualizadas = useMemo(
    () => actualizarPorIndice ? actualizarPorIndice(rowsNormativa, expediente.fallo, expediente.fechaCese, indicesMovilidad, tasaInteresTipo, activeRules, expedienteCalculo) : rowsNormativa,
    [rowsNormativa, expediente.fallo, expediente.fechaCese, indicesMovilidad, tasaInteresTipo, activeRules, expedienteCalculo]
  )
  const rowsVista = useMemo(() => (Array.isArray(rowsEditadas) && rowsEditadas.length ? rowsEditadas : rowsActualizadas), [rowsEditadas, rowsActualizadas])
  useEffect(() => {
    setRowsEditadas(null)
  }, [rowsActualizadas])
  useEffect(() => {
    setDataTablePage(1)
  }, [rows?.length, rowsVista?.length])

  const { caseValidation, blockingIssuesStageA, blockingIssuesStageB, wizardStageABValid, warningsAcknowledged, setWarningsAcknowledged } = useCaseValidation(
    expediente,
    activeRules,
    expedienteCalculo,
    (forced) => setExpediente((prev) => prev.fallo !== forced ? { ...prev, fallo: forced } : prev),
  )

  const onSolicitarGenerarCalculo = () => {
    if (calculoLoading || !canGenerarCalculo) return
    if (caseValidation.warnings.length > 0 && !warningsAcknowledged) return
    setShowCalculoConfirm(true)
  }

  const base120Rows = useMemo(
    () => rowsVista.filter((r) => r.incluirEnPromedio !== false).slice().sort((a, b) => String(a.periodoKey || '').localeCompare(String(b.periodoKey || ''))).slice(-120),
    [rowsVista]
  )
  const invalidBaseRows = useMemo(() => base120Rows.filter((r) => r.invalidIndex), [base120Rows])
  const invalidBaseRowsEffective = useMemo(() => {
    if (!simulationFlowActive) return invalidBaseRows
    return invalidBaseRows.filter((r) => !r.__simulation)
  }, [simulationFlowActive, invalidBaseRows])
  const weakIndexStats = useMemo(() => {
    const sample = base120Rows
    if (!sample.length) return { total: 0, weak: 0, weakPct: 0 }
    const weak = sample.filter((r) => {
      const baseMethod = String(r.indiceBaseMetodo || '')
      const ceseMethod = String(r.indiceCeseMetodo || '')
      return ['carry_forward', 'backfill', 'first_available', 'missing'].includes(baseMethod)
        || ['carry_forward', 'backfill', 'first_available', 'missing'].includes(ceseMethod)
    }).length
    const weakPct = (weak / sample.length) * 100
    return { total: sample.length, weak, weakPct: Number(weakPct.toFixed(2)) }
  }, [base120Rows])
  const weakIndexPctEffective = useMemo(() => (simulationFlowActive ? 0 : weakIndexStats.weakPct), [simulationFlowActive, weakIndexStats.weakPct])
  const updaterWarnings = useMemo(() => (Array.isArray(indicesMovilidad?.__updaterWarnings) ? indicesMovilidad.__updaterWarnings : []), [indicesMovilidad])
  const rangoIndicesError = useMemo(() => {
    if (!invalidBaseRowsEffective.length) return ''
    const sorted = invalidBaseRowsEffective.map((r) => String(r.periodoKey || '')).sort()
    return `${sorted[0]} a ${sorted[sorted.length - 1]}`
  }, [invalidBaseRowsEffective])
  const escenariosLitigio = useMemo(() => {
    if (!actualizarPorIndice) return { escenarios: [], mejorIndice: 'ISBIC' }
    const escenarios = ['ISBIC', 'RIPTE', 'IPC'].map((indice) => {
      const rowsEscenario = actualizarPorIndice(rowsNormativa, indice, expediente.fechaCese, indicesMovilidad, tasaInteresTipo, activeRules, expedienteCalculo)
      const filasValidas = rowsEscenario.filter((r) => !r.invalidoPorCese)
      const haberInicial = filasValidas.reduce((acc, r) => acc + Number(r.montoActualizado || 0), 0)
      const retroactivoConInteres = filasValidas.reduce((acc, r) => acc + Number(r.acumuladoConInteres || 0), 0)
      return {
        indice,
        rows: rowsEscenario,
        haberInicial,
        retroactivoConInteres,
      }
    })

    const mejor = escenarios.reduce((top, s) => (s.haberInicial > (top?.haberInicial || 0) ? s : top), null)
    return { escenarios, mejorIndice: mejor?.indice || 'ISBIC' }
  }, [rowsNormativa, expediente.fechaCese, indicesMovilidad, tasaInteresTipo, activeRules, expedienteCalculo])
  const impactoTasa = useMemo(() => {
    if (!actualizarPorIndice) return { totalPasiva: 0, totalActiva: 0, diferencia: 0 }
    const pasivaRows = actualizarPorIndice(rowsNormativa, expediente.fallo, expediente.fechaCese, indicesMovilidad, 'PASIVA_14290', activeRules, expedienteCalculo)
    const activaRows = actualizarPorIndice(rowsNormativa, expediente.fallo, expediente.fechaCese, indicesMovilidad, 'ACTIVA_JUZGADO', activeRules, expedienteCalculo)
    const totalPasiva = pasivaRows.reduce((acc, r) => acc + Number(r.acumuladoConInteres || 0), 0)
    const totalActiva = activaRows.reduce((acc, r) => acc + Number(r.acumuladoConInteres || 0), 0)
    return {
      totalPasiva,
      totalActiva,
      diferencia: totalActiva - totalPasiva,
    }
  }, [rowsNormativa, expediente.fallo, expediente.fechaCese, indicesMovilidad, activeRules, expedienteCalculo])
  const laborIntegrity = useMemo(() => evaluateLaborIntegrity ? evaluateLaborIntegrity(rowsNormativa) : { blocking: false, status: 'unknown', reasonCodes: [] }, [rowsNormativa])
  const legalConsistency = useMemo(() => {
    if (modoHerramienta === 'base') return { passed: true, severity: 'baja', issues: [] }
    return evaluateLegalConsistency ? evaluateLegalConsistency(rowsVista, expedienteCalculo, activeRules) : { passed: true, severity: 'baja', issues: [] }
  }, [modoHerramienta, rowsVista, expedienteCalculo, activeRules])
  const mixedTagInconsistencyCount = useMemo(() => rowsVista.filter((r) => {
    const hasRentaAut = String(r?.fuenteNormativa || '') === 'renta_monotributo'
    const reg = String(r?.regimenContributivo || '').toUpperCase()
    return hasRentaAut && reg !== 'AUTONOMO'
  }).length, [rowsVista])
  const traceabilityComplete = useMemo(() => {
    const first = rowsVista[0] || {}
    const indexVersion = String(
      first.indiceVersion
      || indicesMovilidad?.__canonical?.version
      || (simulationFlowActive ? 'SIM-INDEX-v1' : '')
    ).trim()
    const cutDate = String(
      first.fechaCorteCalculo
      || expedienteCalculo?.fechaCorteCalculo
      || expediente?.fechaCese
      || ''
    ).trim()
    const normVersion = String(
      normativeVersion
      || (simulationFlowActive ? 'SIM-NORMATIVA-v1' : '')
    ).trim()
    return Boolean(normVersion && indexVersion && cutDate)
  }, [rowsVista, normativeVersion, indicesMovilidad, simulationFlowActive, expedienteCalculo?.fechaCorteCalculo, expediente?.fechaCese])
  const cuitLegalValido = useMemo(() => /^\d{11}$/.test(String(expediente?.cuilCuit || '').trim()), [expediente?.cuilCuit])
  const criticalAlertsCount = useMemo(() => {
    let count = 0
    if (invalidBaseRowsEffective.length > 0) count += 1
    if (weakIndexPctEffective > 35) count += 1
    if (!traceabilityComplete) count += 1
    if (laborIntegrity.blocking) count += 1
    if (!legalConsistency.passed) count += 1
    return count
  }, [invalidBaseRowsEffective.length, weakIndexPctEffective, traceabilityComplete, laborIntegrity.blocking, legalConsistency.passed])
  const contextoDictamenPreview = useMemo(() => composeDictamenContext
    ? composeDictamenContext(rowsVista, expediente, {
      mode: modoHerramienta,
      divisorPolicy: String(activeRules?.protocol?.divisorPolicy || 'legal_120'),
      normativeVersion,
      confiscatoriedadThresholdPct: Number(activeRules?.thresholds?.confiscatoriedad || 0.15),
      indicesMovilidad,
      fechaCorteCalculo: expedienteCalculo?.fechaCorteCalculo || null,
      activeRules,
      preEmisionAudit: null,
      consistenciaJuridica: legalConsistency,
    })
    : null
  , [rowsVista, expediente, modoHerramienta, activeRules, normativeVersion, indicesMovilidad, expedienteCalculo?.fechaCorteCalculo, legalConsistency])
  const protocoloConfiscatoriedadPayload = useMemo(() => {
    const prestaciones = contextoDictamenPreview?.resumenEjecutivo?.prestaciones || {}
    const movilidad = Number(prestaciones.movilidadAcumulada || 1)
    const pbuHist = Number(prestaciones.pbu || 0)
    const pcHist = Number(prestaciones.pc || 0)
    const papHist = Number(prestaciones.pap || 0)
    const pbuReaj = pbuHist * movilidad
    const pcReaj = pcHist * movilidad
    const papReaj = papHist * movilidad

    const dualTotals = (Array.isArray(rowsVista) ? rowsVista : []).reduce((acc, r) => {
      const remTotal = Number(
        r?.remTotal ||
        r?.montoRealSinTope ||
        r?.montoDeclarado ||
        r?.montoOriginal ||
        0,
      )
      const remImpSS = Number(
        r?.remImpSS ||
        r?.montoSujetoAAporte ||
        r?.monto ||
        0,
      )
      acc.remTotal += Number.isFinite(remTotal) ? remTotal : 0
      acc.remImpSS += Number.isFinite(remImpSS) ? remImpSS : 0
      return acc
    }, {remTotal: 0, remImpSS: 0})

    const dualFactor = dualTotals.remTotal > 0 ?
      Math.min(1, Math.max(0, dualTotals.remImpSS / dualTotals.remTotal)) :
      1

    return {
      componentesConTope: {
        pbu: Number(pbuReaj.toFixed(2)),
        pc: Number(pcReaj.toFixed(2)),
        pap: Number(papReaj.toFixed(2)),
      },
      componentesSinTope: {
        pbu: Number((pbuReaj / Math.max(0.01, dualFactor)).toFixed(2)),
        pc: Number((pcReaj / Math.max(0.01, dualFactor)).toFixed(2)),
        pap: Number((papReaj / Math.max(0.01, dualFactor)).toFixed(2)),
      },
      dualMontos: {
        remTotal: Number(dualTotals.remTotal.toFixed(2)),
        remImpSS: Number(dualTotals.remImpSS.toFixed(2)),
        dualFactor: Number(dualFactor.toFixed(6)),
      },
    }
  }, [contextoDictamenPreview?.resumenEjecutivo?.prestaciones, rowsVista])
  const topeGlobalAudit = useMemo(() => evaluateHaberMaximoConfiscatoriedad
    ? evaluateHaberMaximoConfiscatoriedad(
      contextoDictamenPreview?.resumenEjecutivo?.haberBolsilloReajustado || 0,
      expedienteCalculo?.fechaCorteCalculo || new Date().toISOString(),
      Number(activeRules?.thresholds?.confiscatoriedad || 0.15),
      protocoloConfiscatoriedadPayload,
      activeRules?.caseMeta?.exentoTopeArt9,
    )
    : null
  , [contextoDictamenPreview?.resumenEjecutivo?.haberBolsilloReajustado, expedienteCalculo?.fechaCorteCalculo, activeRules?.thresholds?.confiscatoriedad, activeRules?.caseMeta?.exentoTopeArt9, protocoloConfiscatoriedadPayload])
  const docenteConfigBlocking = useMemo(() => {
    const isDocente = String(activeRules?.caseMeta?.scenarioKey || '').includes('DOCENTE') || Boolean(expediente?.esDocente)
    if (!isDocente) return false
    const sueldo = Number(expediente?.datosDocentes?.sueldoBrutoCargoCese || 0)
    const m60 = Number(expediente?.datosDocentes?.mesesEnCargoContinuos || 0)
    const m120 = Number(expediente?.datosDocentes?.mesesEnCargoAlternados || 0)
    return !(sueldo > 0 && (m60 >= 60 || m120 >= 120))
  }, [activeRules?.caseMeta?.scenarioKey, expediente?.esDocente, expediente?.datosDocentes?.sueldoBrutoCargoCese, expediente?.datosDocentes?.mesesEnCargoContinuos, expediente?.datosDocentes?.mesesEnCargoAlternados])
  const serieStatus = useMemo(() => getSerieStatus ? getSerieStatus(tasaInteresTipo) : { upToDate: true, ultimaMesAnio: '' }, [tasaInteresTipo])
  const preEmisionAudit = useMemo(() => buildPreEmissionAudit ? buildPreEmissionAudit({
    weakIndexPct: weakIndexPctEffective,
    invalidIndexRows: invalidBaseRowsEffective.length,
    traceabilityComplete,
    integrityBlocking: laborIntegrity.blocking,
    integrityStatus: laborIntegrity.status,
    integrityReasonCodes: laborIntegrity.reasonCodes,
    criticalAlerts: criticalAlertsCount,
    legalConsistencyBlocking: !legalConsistency.passed,
    topeGlobalAudit,
    serieTemporalIncompleta: simulationFlowActive ? false : !serieStatus.upToDate,
    docenteConfigBlocking,
  }) : null, [weakIndexPctEffective, invalidBaseRowsEffective.length, traceabilityComplete, laborIntegrity.blocking, laborIntegrity.status, laborIntegrity.reasonCodes, criticalAlertsCount, legalConsistency.passed, topeGlobalAudit, serieStatus.upToDate, docenteConfigBlocking, simulationFlowActive])
  const contextoDictamen = useMemo(() => composeDictamenContext ? composeDictamenContext(rowsVista, expediente, {
    mode: modoHerramienta,
    divisorPolicy: String(activeRules?.protocol?.divisorPolicy || 'legal_120'),
    normativeVersion,
    confiscatoriedadThresholdPct: Number(activeRules?.thresholds?.confiscatoriedad || 0.15),
    indicesMovilidad,
    fechaCorteCalculo: expedienteCalculo?.fechaCorteCalculo || null,
    activeRules,
    preEmisionAudit,
    topeGlobalAudit,
    consistenciaJuridica: legalConsistency,
  }) : null, [rowsVista, expediente, modoHerramienta, activeRules, normativeVersion, indicesMovilidad, expedienteCalculo?.fechaCorteCalculo, preEmisionAudit, topeGlobalAudit, legalConsistency])
  const faltaHaberActual = Number(expediente.haberActual || 0) <= 0
  const canGenerarCalculo = rows.length > 0 && Number(coins.coinsBalance || 0) >= 2 && !calculoLoading && !laborIntegrity.blocking
  const laborBlockingReason =
    laborIntegrity.blockingIssues?.[0] ||
    laborIntegrity.issues?.[0] ||
    'revisar historia laboral.'
  const motivoBloqueoCalculo = !rows.length
    ? 'Carga historial laboral antes de calcular.'
    : Number(coins.coinsBalance || 0) < 2
      ? 'Saldo insuficiente: necesitás 2 coins para generar el cálculo.'
      : laborIntegrity.blocking
        ? `Integridad laboral bloqueante: ${laborBlockingReason}`
      : !legalConsistency.passed
        ? `Consistencia jurídica pendiente: ${legalConsistency.issues[0] || 'revisar fundamentos legales del caso.'}`
      : ''
  const calidadIndicesBloqueante = modoHerramienta === 'avanzado' && weakIndexPctEffective > 35
  const contrasteNoPerjuicio = useMemo(() => {
    const haberActual = Number(expediente?.haberActual || 0)
    const haberReajustado = Number(contextoDictamen?.resumenEjecutivo?.haberBolsilloReajustado || 0)
    const diferencia = haberReajustado - haberActual
    const impactoPct = haberActual > 0 ? (diferencia / haberActual) * 100 : 0
    return { haberActual, haberReajustado, diferencia, impactoPct }
  }, [expediente?.haberActual, contextoDictamen?.resumenEjecutivo?.haberBolsilloReajustado])
  const umbralConfiscatoriedadPct = Number(activeRules?.thresholds?.confiscatoriedad || 0.15) * 100
  const noPerjuicioSimulationError = Number(contrasteNoPerjuicio.impactoPct || 0) < 0
  const noPerjuicioEconomico = !noPerjuicioSimulationError && Number(contrasteNoPerjuicio.impactoPct || 0) < umbralConfiscatoriedadPct
  const canGenerarDictamen = calculoReady && invalidBaseRowsEffective.length === 0 && (modoHerramienta === 'base' || !faltaHaberActual) && !dictamenLoading && !calidadIndicesBloqueante && preEmisionAudit.allowAdvancedDictamen && (modoHerramienta === 'base' || cuitLegalValido) && (modoHerramienta === 'base' || !noPerjuicioEconomico || allowLitigioArtesanalBypass) && !mutationsQualityAudit.hasWeakJustifications && !indicesUnavailable
  const motivoBloqueoDictamen = !cuitLegalValido && modoHerramienta !== 'base'
    ? 'CUIT requerido para validación legal.'
    : indicesUnavailable
      ? 'Dictamen bloqueado: series de índices oficiales incompletas para 2001-2021.'
    : noPerjuicioSimulationError && modoHerramienta !== 'base'
      ? 'Error de simulación: impacto negativo detectado. Revisá ruta de fórmula y parámetros del expediente.'
    : noPerjuicioEconomico && !allowLitigioArtesanalBypass && modoHerramienta !== 'base'
      ? 'Juicio Inviable: No se detecta perjuicio económico.'
      : !calculoReady
    ? 'Generá el cálculo primero para emitir informe o dictamen.'
    : invalidBaseRowsEffective.length > 0
      ? 'Corregí los índices faltantes para habilitar dictamen.'
      : calidadIndicesBloqueante
        ? `Calidad de índices insuficiente (${weakIndexPctEffective}% con fallback fuerte en base 120).`
      : faltaHaberActual && modoHerramienta !== 'base'
        ? 'Completa el haber mensual actual para habilitar dictamen.'
        : !preEmisionAudit.allowAdvancedDictamen
          ? `Semáforo ${preEmisionAudit.color.toUpperCase()}: ${(preEmisionAudit.reasons || []).join(' | ') || 'auditoría pre-emisión bloqueante.'}`
          : mutationsQualityAudit.hasWeakJustifications
            ? `Dictamen bloqueado: hay ${mutationsQualityAudit.invalidCount} ajustes manuales con justificación insuficiente (mínimo ${MIN_REASON_DETAIL_LEN} caracteres).`
          : ''
  const retroactivoEstimado = useMemo(
    () => rowsVista.reduce((acc, r) => acc + Number(r.acumuladoConInteres || 0), 0),
    [rowsVista]
  )
  const edadDetectada = useMemo(() => {
    if (!expediente.fechaNacimiento) return 0
    const nacimiento = new Date(expediente.fechaNacimiento)
    if (Number.isNaN(nacimiento.getTime())) return 0
    const hoy = new Date()
    let edad = hoy.getFullYear() - nacimiento.getFullYear()
    const m = hoy.getMonth() - nacimiento.getMonth()
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad -= 1
    return Math.max(0, edad)
  }, [expediente.fechaNacimiento])
  const aniosTareasCuidado = useMemo(() => {
    const sexo = String(expediente.sexo || '').toUpperCase()
    if (sexo !== 'F' && sexo !== 'X') return 0
    const hijos = Number(expediente.cantidadHijos || 0)
    const adoptivos = Number(expediente.hijosAdoptivos || 0)
    const discapacidad = Number(expediente.hijosDiscapacidad || 0)
    const a = expediente.cobraAUH === 'si' ? 1 : 0
    return Math.max(0, hijos + adoptivos * 2 + discapacidad + a)
  }, [expediente])

  const diasAporteResumen = (row) => {
    const diasRaw = Number(row?.diasComputables ?? row?.diasOrigen ?? row?.dias)
    const dias = Number.isFinite(diasRaw) ? Math.trunc(diasRaw) : null
    const computable = Number(row?.monto ?? row?.remImpSS ?? row?.montoOriginal ?? 0)
    const declarado = Number(row?.montoDeclarado ?? computable)
    const hasMonto = computable > 0 || declarado > 0
    const diPardoConsistente =
      String(row?.escenarioNormalizacionMesParcial || '') ===
      'sueldo_completo_consistente'

    if (dias && dias > 0) {
      if (dias < 30 && diPardoConsistente && hasMonto) return 30
      return Math.min(30, dias)
    }
    if (hasMonto) return 30
    return 0
  }

  const aniosAportesBase = useMemo(() => {
    let totalDias = 0
    ;(Array.isArray(rowsVista) ? rowsVista : []).forEach((r) => {
      const key = String(r.periodoKey || r.periodoISO || r.periodo || '').trim()
      if (!key) return
      if (r.invalidoPorCese) return
      totalDias += diasAporteResumen(r)
    })
    return totalDias / 365
  }, [rowsVista])
  const aniosAportesTotales = useMemo(() => aniosAportesBase + aniosTareasCuidado, [aniosAportesBase, aniosTareasCuidado])
  const edadObjetivo = useMemo(() => (String(expediente.sexo || 'F').toUpperCase() === 'M' ? 65 : 60), [expediente.sexo])
  const faltanAniosAporte = useMemo(() => Math.max(0, 30 - aniosAportesTotales), [aniosAportesTotales])
  const faltanMesesAporte = useMemo(() => {
    if (aniosAportesTotales >= 30) return 0
    return Math.ceil((30 - aniosAportesTotales) * 12)
  }, [aniosAportesTotales])
  const estadoJubilatorio = useMemo(() => {
    if (edadDetectada >= edadObjetivo && faltanAniosAporte <= 0) return 'En condiciones de jubilarse'
    return `Faltan ${faltanMesesAporte} meses`
  }, [edadDetectada, edadObjetivo, faltanAniosAporte, faltanMesesAporte])
  const modoCaso = useMemo(() => {
    const judicializado = Boolean(expediente?.tieneBeneficioPrevio)
    return {
      key: judicializado ? 'JUDICIALIZADO' : 'PLANIFICACION',
      label: judicializado ? 'Modo Judicializado' : 'Modo Planificacion Inicial',
      badgeClass: judicializado ?
        'border-amber-300/50 bg-amber-500/20 text-amber-100' :
        'border-cyan-300/50 bg-cyan-500/20 text-cyan-100',
    }
  }, [expediente?.tieneBeneficioPrevio])
  const isCoinsPage = window.location.pathname.startsWith('/coins')
  const {
    formStep, setFormStep,
    step1Valid, step2Valid, step3Valid,
    goNext, goBack, goToStep,
  } = useWizard({
    expediente,
    modoHerramienta,
    jurisdiccion,
    activeRules,
  })

  const manualRowsValid = useMemo(() => {
    if (!Array.isArray(manualRowsDraft) || manualRowsDraft.length === 0) return false
    return manualRowsDraft.some((r) => String(r.periodo || '').trim())
  }, [manualRowsDraft])

  const step4Props = useStep4({
    modoHerramienta, cargaHistorialModo, setCargaHistorialModo,
    simulationEnabled, rows, setRows, rawRows, setRawRows,
    selectedCaseId, setSelectedCaseId, simulationCases, loadSimulationCase,
    caseData, resetRemainingSession, setUltimoPeriodoDetectado,
    extractPdfHeaderCese, setFechaCeseCabeceraPdf,
    toFechaInputFromPeriodoKey, expediente, setExpediente,
    resetCaseSession, setFechaCeseSugerida,
    setActividadFuentes, titularCuilOrigen, setTitularCuilOrigen,
    processParserOutput, activeRules, normalizeReviewRows,
    setManualResumenTotalesDraft, setCalculoReady,
    setProgressLabel, progressLabel,
    manualRowsDraft, updateManualRow, addManualRow, manualRowsValid, loadManualRows,
    sanitizeExpedienteScenarioForPdfIngestion,
    reviewTab, setReviewTab, reviewMode, setReviewMode,
    resumenHistorialLaboralRows,
    scoreCertezaInforme, shadowBilling, CORE_RULES_CONFIG,
    shadowTopConflicts, setSelectedConflictCuit,
    shadowResumenMeta, manualResumenTotalesDraft,
    updateManualResumenRow, addManualResumenRow,
    applyManualResumenTotales, conciliacionManualActa,
    periodosResumenControl, downloadProcessingLog, processingPayloadAt,
    showManualRangePanel, setShowManualRangePanel,
    manualRangeDraft, setManualRangeDraft, applyManualRange,
    groupedByYearFiltered, expandedYears, setExpandedYears,
    updateRowField: updateRowFieldWithAudit, removeRevisionRow,
    mutationsLog, mutationsIntegrityHash, mutationsQualityAudit,
    copyHashFeedback, setCopyHashFeedback,
    showMutationsAccordion, setShowMutationsAccordion,
    minusMonthsIso, expedienteCalculo,
    prorrateoServiciosDraft, updateProrrateoServicio,
    addProrrateoServicio, applyProrrateoServicios,
    rowsVista, topeGlobalAudit, mirrorRows,
    goBack, caseValidation, warningsAcknowledged,
    setWarningsAcknowledged, onSolicitarGenerarCalculo,
    canGenerarCalculo, calculoLoading, COINS_POR_CALCULO,
    motivoBloqueoCalculo, calculoFetchError, setCalculoFetchError,
  })

  const resultsViewProps = useResultsView({
    formStep, calculoReady, rows, invalidBaseRows, updaterWarnings,
    calidadIndicesBloqueante, mixedTagInconsistencyCount, rangoIndicesError,
    weakIndexStats, laborIntegrity, modoHerramienta, faltaHaberActual,
    rowsVista, indicesUnavailable, activeInfoTab, setActiveInfoTab, goToStep,
    empleadores, retroactivoEstimado, contextoDictamen,
    indicesLoading, tasaInteresTipo, setTasaInteresTipo,
    TASAS_DISPONIBLES, setExpediente, expediente,
    escenariosLitigio, impactoTasa, serieStatus, activeRules,
    preEmisionAudit, canGenerarDictamen, dictamenLoading,
    noPerjuicioEconomico, allowLitigioArtesanalBypass,
    contrasteNoPerjuicio, umbralConfiscatoriedadPct,
    noPerjuicioSimulationError, motivoBloqueoDictamen,
    onGenerarDictamen, formatArs, setMostrarViabilidad,
    registrarBypassLitigioArtesanal, rowsNormativa,
    showAllDetalle, setShowAllDetalle,
    dataTablePage, setDataTablePage, setRowsEditadas,
    ultimoPeriodoDetectado, modoCaso, edadDetectada, aniosTareasCuidado,
    aniosAportesTotales, estadoJubilatorio,
    normativeVersion, normativeAudit, faltanMesesAporte,
    mostrarViabilidad,
  })

  // --- Datos para las nuevas vistas Ciudadano / Abogado ---
  const citizenData = useCitizenData({ contextoDictamen, expediente, activeRules, rows })
  const lawyerData = useLawyerData({ contextoDictamen, expediente, activeRules, rows })

  useEffect(() => {
    if (!calculoReady) return
    setCalculoReady(false)
  }, [
    expediente.haberActual,
    expediente.fechaCese,
    expediente.fechaOtorgamientoBeneficio,
    expediente.fechaReclamoAdministrativo,
    expediente.fechaDenegatoriaODemanda,
  ])

  const hasBaches = useMemo(() => rowsNormativa.some((r) => r.requiereRevision), [rowsNormativa])

  useEffect(() => {
    const aplica = expediente.sexo === 'F' || expediente.sexo === 'X'
    const sinCargaCuidado = !expediente.cantidadHijos && !expediente.hijosAdoptivos && !expediente.hijosDiscapacidad && expediente.cobraAUH !== 'si'
    if (hasBaches && aplica && sinCargaCuidado) {
      setShowTareasCuidadoModal(true)
    }
  }, [hasBaches, expediente.sexo, expediente.cantidadHijos, expediente.hijosAdoptivos, expediente.hijosDiscapacidad, expediente.cobraAUH])

  if (!engineReady) {
    return (
      <div className={`min-h-screen font-sans ${COLOR_PROFILES[colorProfile]?.isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'}`}>
        {themeToggle}
        <AnimatedBackground />
        <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-4">
          <div className="panel-profile rounded-2xl px-6 py-4 text-sm text-slate-200">Cargando motor previsional...</div>
        </div>
      </div>
    )
  }

  if (!auth.authReady) {
    return (
      <div className={`min-h-screen font-sans ${COLOR_PROFILES[colorProfile]?.isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'}`}>
        {themeToggle}
        <AnimatedBackground />
        <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-4">
          <div className="panel-profile rounded-2xl px-6 py-4 text-sm text-slate-200">Restaurando sesión...</div>
        </div>
      </div>
    )
  }

  if (!auth.isUserAuthenticated) {
    return (
      <>
        {themeToggle}
        <Landing
          onLoginSuccess={auth.onLoginSuccess}
          onLogout={auth.onLogout}
          isAuthenticated={auth.isUserAuthenticated}
          userCuit={auth.userRef}
          userDisplayName={auth.userDisplayName}
          isAdmin={auth.isAdmin}
          onGoToAdmin={() => { window.location.href = '/master' }}
        />
      </>
    )
  }

  if (isCoinsPage) {
    return (
      <div className={`min-h-screen font-sans ${COLOR_PROFILES[colorProfile]?.isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'}`}>
        {themeToggle}
        <AnimatedBackground />
        <CoinMarketplace userRef={auth.userRef} />
      </div>
    )
  }

  const mustCompletePasswordSetup = Boolean(auth.forcePasswordSetup || auth.forcePasswordChange)

  return (
    <div className={`min-h-screen font-sans ${COLOR_PROFILES[colorProfile]?.isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'}`}>
      {themeToggle}
      <AnimatedBackground />
      {mustCompletePasswordSetup && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md">
          <div className="w-full max-w-md">
            <UserLogin
              onLoginSuccess={auth.onLoginSuccess}
              onLogout={auth.onLogout}
              isAuthenticated={auth.isUserAuthenticated}
              userCuit={auth.userRef}
              userDisplayName={auth.userDisplayName}
              isAdmin={auth.isAdmin}
              forcePasswordSetup={auth.forcePasswordSetup}
              forcePasswordChange={auth.forcePasswordChange}
              coinsBalance={coins.coinsBalance}
              openTickets={openTickets}
              hasAdminUpdates={hasAdminUpdates}
              coinsLoading={coins.isLoading}
              onBuyMore={() => {}}
              onOpenSupport={() => {}}
              onOpenTickets={() => {}}
              onPasswordSetupResolved={() => { auth.setForcePasswordSetup(false); auth.setForcePasswordChange(false) }}
              onGoToAdmin={() => {}}
            />
          </div>
        </div>
      )}
      <div>
        <main className="w-full px-3 py-6 md:px-4 xl:px-6">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[260px_minmax(0,1fr)_260px] 2xl:grid-cols-[280px_minmax(0,1fr)_280px]">
            <aside className="xl:sticky xl:top-4 xl:self-start">
              <div className="panel-profile rounded-2xl p-3 backdrop-blur-2xl">
                <UserLogin
                  onLoginSuccess={auth.onLoginSuccess}
                  onLogout={auth.onLogout}
                  isAuthenticated={auth.isUserAuthenticated}
                  userCuit={auth.userRef}
                  userDisplayName={auth.userDisplayName}
                  isAdmin={auth.isAdmin}
                  forcePasswordSetup={auth.forcePasswordSetup}
                  forcePasswordChange={auth.forcePasswordChange}
                  coinsBalance={coins.coinsBalance}
                  openTickets={openTickets}
                  hasAdminUpdates={hasAdminUpdates}
                  coinsLoading={coins.isLoading}
                   onBuyMore={() => { if (auth.userRef) window.location.href = `/coins?user=${encodeURIComponent(auth.userRef)}` }}
                  onOpenSupport={() => setShowSupportModal(true)}
                  onOpenTickets={openTicketsCenter}
                  onPasswordSetupResolved={() => { auth.setForcePasswordSetup(false); auth.setForcePasswordChange(false) }}
                  onGoToAdmin={() => { window.location.href = '/master' }}
                />
              </div>
            </aside>

            <section className="min-w-0 xl:px-2">
          <div className="panel-profile rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-2xl">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-3">
                <div className="panel-profile rounded-xl p-3">
                  <div className="flex items-center justify-between text-xs text-slate-200">
                    <span>Progreso de carga</span>
                    <div className="flex items-center gap-3">
                      {formStep >= 1 && (
                        <button type="button" onClick={handleReiniciar} className="rounded-lg border border-white/20 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition-colors">
                          Reiniciar
                        </button>
                      )}
                      {formStep === 0 ? (
                        <span>Paso 1 de 6: Selección de modo</span>
                      ) : (
                        <span>Paso {formStep + 1} de 6</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white/10">
                    <div className="h-2 rounded-full panel-profile-accent transition-all" style={{ width: `${((formStep + 1) / 6) * 100}%` }} />
                  </div>
                </div>

                {formStep >= 1 && (
                  <div className="panel-profile rounded-xl p-3 flex items-center justify-between">
                    <span className="text-xs text-slate-300">
                      Modo:{' '}
                      <span className={modoHerramienta === 'base' ? 'text-profile-ok font-semibold' : 'text-profile-warn font-semibold'}>
                        {modoHerramienta === 'base' ? 'Calculadora Base' : 'Modo Avanzado'}
                      </span>
                    </span>
                    <span className="text-[11px] text-slate-400">
                      Guardado local automático: {lastLocalSaveAt ? `última copia ${lastLocalSaveAt}` : 'activo'}
                    </span>
                  </div>
                )}
                {(rows.length > 0 || rawRows.length > 0) && formStep >= 1 && (
                  <div className="flex justify-end">
                    <button type="button" onClick={handleNuevoCaso} className="rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-100">
                      Iniciar nuevo caso (limpiar)
                    </button>
                  </div>
                )}
                {caseResetNotice && (
                  <div className="rounded-xl border border-cyan-300/45 bg-cyan-500/15 px-3 py-2 text-xs text-cyan-100">
                    {caseResetNotice}
                  </div>
                )}

                {formStep === 0 && (
                  <section className="panel-profile rounded-xl p-4">
                    <p className="mb-1 text-base font-bold text-white">Selección de modo</p>
                    <p className="mb-3 text-xs text-slate-300">Elegí el modo de trabajo para comenzar:</p>
                    <div className="flex flex-wrap gap-3">
<button
  type="button"
  onClick={() => {
    setModoHerramienta('base')
    setJurisdiccion('ANSeS_NACIONAL')
    setExpediente((p) => ({
      ...p,
      esDocente: false,
      tieneBeneficioPrevio: false,
      datosDocentes: { cargoCese: '', sueldoBrutoCargoCese: '', mesesEnCargoContinuos: 0, mesesEnCargoAlternados: 0 },
      datosPension: { tipoPension: '', fechaFallecimiento: '', haberJubiladoCausante: '', concurreConyugeConviviente: true, concurrentes: { conyuge: true, hijosCount: 0 } },
      fechaReclamoAdministrativo: '',
      fechaDenegatoriaODemanda: '',
      fechaOtorgamientoBeneficio: '',
      haberActual: '',
      cobraAUH: 'no',
      hijosAdoptivos: '',
      hijosDiscapacidad: '',
    }))
    goToStep(1)
  }}
  className="flex-1 min-w-[200px] rounded-xl border border-white/20 bg-white/5 px-5 py-4 text-left hover:bg-white/10 transition-colors"
>
  <p className="text-sm font-semibold text-profile-ok">Calculadora Base</p>
  <p className="mt-1 text-[11px] text-slate-300">Índice neutro para estimar haber sin estrategia litigiosa. El abogado decide luego si judicializa.</p>
</button>
                      <button
                        type="button"
                        onClick={() => { setModoHerramienta('avanzado'); goToStep(1) }}
                        className="flex-1 min-w-[200px] rounded-xl border border-white/20 bg-white/5 px-5 py-4 text-left hover:bg-white/10 transition-colors"
                      >
                        <p className="text-sm font-semibold text-profile-warn">Modo Avanzado</p>
                        <p className="mt-1 text-[11px] text-slate-300">Selección de fallo/índice, escenarios de litigio y análisis estratégico completo.</p>
                      </button>
                    </div>
                    <p className="mt-3 text-[11px] text-slate-400">
                      Guardado local automático: {lastLocalSaveAt ? `última copia ${lastLocalSaveAt}` : 'activo'}
                    </p>
                  </section>
                )}

                {formStep === 1 && (
                  <section className="panel-profile rounded-xl p-4">
                    <p className="mb-1 text-xs uppercase tracking-wide text-profile-accent">Tarjeta 1 - Datos del Titular Jubilatorio</p>
                    <p className="mb-3 text-xs text-slate-200">Completa identificación del titular. Estos datos se usan para validaciones, edad y cabecera del dictamen.</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input title="Apellido del titular tal como figura en expediente." type="text" value={expediente.apellido} onChange={(e) => setExpediente((p) => ({ ...p, apellido: e.target.value }))} placeholder="Apellido" className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
                      <input title="Nombre del titular tal como figura en expediente." type="text" value={expediente.nombre} onChange={(e) => setExpediente((p) => ({ ...p, nombre: e.target.value }))} placeholder="Nombre" className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
                      {modoHerramienta === 'avanzado' && (
  <input title="CUIL/CUIT obligatorio para identificar el caso previsional." type="text" value={expediente.cuilCuit} onChange={(e) => setExpediente((p) => ({ ...p, cuilCuit: e.target.value.replace(/\D/g, '') }))} placeholder="CUIL/CUIT" maxLength={11} className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
)}
                      <input title="Se usa para calcular edad y elegibilidad jubilatoria." type="date" value={expediente.fechaNacimiento} onChange={(e) => setExpediente((p) => ({ ...p, fechaNacimiento: e.target.value }))} className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white" />
                      <select title="Determina edad objetivo y reglas de tareas de cuidado." value={expediente.sexo} onChange={(e) => setExpediente((p) => ({ ...p, sexo: e.target.value }))} className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white">
                        <option value="F">Sexo: Femenino</option>
                        <option value="M">Sexo: Masculino</option>
                        <option value="X">Sexo: X</option>
                      </select>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <button type="button" onClick={goBack} className="rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white">Anterior</button>
                      <button type="button" disabled={!step1Valid} onClick={goNext} className="btn-profile-soft rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-40">Siguiente</button>
                    </div>
                    {!step1Valid && <p className="mt-2 text-xs text-profile-warn">Completa {modoHerramienta === 'avanzado' ? 'apellido, nombre, CUIL/CUIT (11 dígitos), fecha de nacimiento y sexo' : 'apellido, nombre, fecha de nacimiento y sexo'} para avanzar.</p>}
                  </section>
                )}

                {formStep === 2 && (
                  <section className="panel-profile-ok rounded-xl p-4">
                    <p className="mb-1 text-xs uppercase tracking-wide text-profile-ok">Tarjeta 2 - Tareas de Cuidado y Asignación</p>
                    <p className="mb-3 text-xs text-emerald-50/90">Esta información permite sumar servicios computables y reducir faltantes de aportes.</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {(expediente.sexo === 'F' || expediente.sexo === 'X') && (
                        <>
                          <input type="number" min="0" value={expediente.cantidadHijos} onChange={(e) => setExpediente((p) => ({ ...p, cantidadHijos: e.target.value }))} placeholder="Cantidad de hijos" className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
                          {modoHerramienta === 'avanzado' && Number(expediente.cantidadHijos || 0) > 0 && (
                            <>
                              <input type="number" min="0" value={expediente.hijosAdoptivos} onChange={(e) => setExpediente((p) => ({ ...p, hijosAdoptivos: e.target.value }))} placeholder="Hijos adoptivos" className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
                              <input type="number" min="0" value={expediente.hijosDiscapacidad} onChange={(e) => setExpediente((p) => ({ ...p, hijosDiscapacidad: e.target.value }))} placeholder="Hijos con discapacidad" className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white placeholder:text-slate-300" />
                            </>
                          )}
                        </>
                      )}
                      {modoHerramienta === 'avanzado' && (
                        <select title="Impacta en el reconocimiento adicional de servicios por tareas de cuidado." value={expediente.cobraAUH} onChange={(e) => setExpediente((p) => ({ ...p, cobraAUH: e.target.value }))} className="h-10 rounded-xl border border-white/25 bg-slate-900/60 px-3 text-white">
                          <option value="no">No cobró asignación/AUH mayor o igual a 12 meses</option>
                          <option value="si">Cobró asignación/AUH mayor o igual a 12 meses</option>
                        </select>
                      )}
                    </div>
                    {resolveCuitForEngine(expediente.cuilCuit).fallbackApplied && (
                      <p className="mt-2 text-xs text-amber-200">CUIT invalido detectado: se utiliza identificador generico para la simulacion matematica.</p>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <button type="button" onClick={goBack} className="panel-profile rounded-xl px-4 py-2 text-sm font-semibold">Anterior</button>
                      <button type="button" disabled={!step2Valid} onClick={goNext} className="btn-profile-soft rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-40">Siguiente</button>
                    </div>
                  </section>
                )}

                {formStep === 3 && (
                  <EstrategiaJudicialSection
                    modoHerramienta={modoHerramienta}
                    expediente={expediente}
                    setExpediente={setExpediente}
                    jurisdiccion={jurisdiccion}
                    setJurisdiccion={setJurisdiccion}
                    fechaCeseEstimada={fechaCeseEstimada}
                    setFechaCeseEstimada={setFechaCeseEstimada}
                    fechaCeseSugerida={fechaCeseSugerida}
                    goBack={goBack}
                    goNext={goNext}
                    step3Valid={step3Valid}
                    wizardStageABValid={wizardStageABValid}
                    blockingIssuesStageA={blockingIssuesStageA}
                    blockingIssuesStageB={blockingIssuesStageB}
                    modoCaso={modoCaso}
                    caseValidation={caseValidation}
                    activeRules={activeRules}
                    parseMontoValue={parseMontoValue}
                  />
                )}

                {formStep === 4 && (
                  <Step4Panel
                    step4Props={step4Props}
                    modoHerramienta={modoHerramienta}
                    rows={rows}
                    reviewTab={reviewTab}
                  />
                )}
              </div>

            </div>
          </div>

          <ResultsAlerts {...resultsViewProps.resultsAlertsProps} />

          {formStep >= 5 && calculoReady && (
            <>
              <div className="mt-4 rounded-2xl border border-white/20 bg-white/10 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setActiveInfoTab('basica')} className={`rounded-xl px-3 py-1.5 text-xs font-semibold ${activeInfoTab === 'basica' ? 'panel-profile-accent text-profile-accent' : 'panel-profile'}`}>Informacion Basica</button>
                  <button type="button" onClick={() => setActiveInfoTab('completa')} className={`rounded-xl px-3 py-1.5 text-xs font-semibold ${activeInfoTab === 'completa' ? 'panel-profile-accent text-profile-accent' : 'panel-profile'}`}>Informacion Completa</button>
                  <div className="flex-1" />
                  <button type="button" onClick={handleReiniciar} className="rounded-xl border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-white/15 transition-colors">
                    Reiniciar caso
                  </button>
                </div>
              </div>

              {rowsVista.length === 0 && (
                <div className="mt-4 rounded-xl border border-white/20 bg-slate-900/60 p-6 text-center">
                  <p className="text-sm text-slate-300">El cálculo se generó pero no hay datos de resultado disponibles.</p>
                  <p className="mt-1 text-xs text-slate-400">Volvé a la carga de historial laboral para revisar los datos.</p>
                  <button type="button" onClick={() => goToStep(4)} className="mt-3 rounded-xl border border-amber-300/45 bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-100">Volver a corregir tabla</button>
                </div>
              )}

              {rowsVista.length > 0 && activeInfoTab === 'basica' && (
                <ResultsShell
                  citizenData={citizenData}
                  lawyerData={lawyerData}
                  defaultMode="citizen"
                >
                  <ResultadosTabBasica {...resultsViewProps.basicaTabProps} />
                </ResultsShell>
              )}

              {rowsVista.length > 0 && activeInfoTab === 'completa' && (
                <ResultadosTabCompleta {...resultsViewProps.completaTabProps} />
              )}
            </>
          )}
            </section>

            <GuideSidebar {...resultsViewProps.sidebarProps} />
          </div>
        </main>

        <footer className="border-t border-white/10 bg-slate-950/60 py-6">
          <div className="max-w-7xl mx-auto flex items-end justify-between gap-4 px-4 text-xs text-slate-300">
            <div>
              <p className="mb-2">© 2026 Plataforma Pericial Previsional</p>
              <div className="mt-3"><Disclaimer type="footer" /></div>
            </div>
            <div className="shrink-0 text-center">
              <img src="/imagen/Di-X.svg" alt="Di-X" className="h-10 w-auto opacity-95" style={{ filter: 'invert(1) brightness(1.5)' }} />
              <p className="mt-1 text-[11px] tracking-wide text-slate-300">Di-X</p>
            </div>
          </div>
        </footer>
      </div>
      <MutationReasonModal
        show={showMutationReasonModal}
        pendingMutationAction={pendingMutationAction}
        mutationReasonDraft={mutationReasonDraft}
        setMutationReasonDraft={setMutationReasonDraft}
        mutationReasonWeak={mutationReasonWeak}
        onClose={() => setShowMutationReasonModal(false)}
        onConfirm={commitMutationAction}
      />
      <AppModals
        showSupportModal={showSupportModal}
        setShowSupportModal={setShowSupportModal}
        showTicketsModal={showTicketsModal}
        setShowTicketsModal={setShowTicketsModal}
        showDictamen={showDictamen}
        setShowDictamen={setShowDictamen}
        dictamenLoading={dictamenLoading}
        dictamenText={dictamenText}
        dictamenVerificationCode={dictamenVerificationCode}
        contextoDictamen={contextoDictamen}
        mutationsLog={mutationsLog}
        showCoinsGate={showCoinsGate}
        setShowCoinsGate={setShowCoinsGate}
        coinsGateRequired={coinsGateRequired}
        cuit={auth.userRef}
        COINS_POR_CALCULO={COINS_POR_CALCULO}
        modoHerramienta={modoHerramienta}
        calculoReady={calculoReady}
        onGenerarCalculo={onGenerarCalculo}
        showCalculoConfirm={showCalculoConfirm}
        setShowCalculoConfirm={setShowCalculoConfirm}
        calculoLoading={calculoLoading}
        showTareasCuidadoModal={showTareasCuidadoModal}
        setShowTareasCuidadoModal={setShowTareasCuidadoModal}
        expediente={expediente}
        titularCuilOrigen={titularCuilOrigen}
        aniosTareasCuidado={aniosTareasCuidado}
        rowsVista={rowsVista}
        shadowBilling={shadowBilling}
        scoreCertezaInforme={scoreCertezaInforme}
        conciliacionManualActa={conciliacionManualActa}
        goToStep={goToStep}
      />
      <ConfirmModal
        show={showNuevoCasoConfirm}
        title="Nuevo caso"
        message="¿Limpiar datos y volver a selección de modo?"
        onConfirm={handleNuevoCasoConfirm}
        onCancel={() => setShowNuevoCasoConfirm(false)}
        confirmLabel="Limpiar y empezar de nuevo"
        cancelLabel="Cancelar"
        variant="danger"
      />
      <ConfirmModal
        show={showReiniciarConfirm}
        title="Reiniciar caso"
        message="¿Reiniciar completamente? Se borrarán todos los datos del caso, incluyendo datos personales, historial laboral y cálculos."
        onConfirm={handleReiniciarConfirm}
        onCancel={() => setShowReiniciarConfirm(false)}
        confirmLabel="Reiniciar todo"
        cancelLabel="Cancelar"
        variant="danger"
      />
    </div>
  )
}
